import Phaser from 'phaser';
import { GAME_W, GAME_H, setupCamera, getZoomX, getZoomY } from '../dpr.js';
import { GameStateManager, type LeaderboardEntry, type InfernoLeaderboardEntry } from '../state/GameStateManager';
import { HERO_OPTIONS } from './HeroSelectScene';
import { HERO_CLASS_NAMES, formatAffix, getZoneForLevel, TOME_DEFINITIONS } from '@roe/shared';
import type { Sigil } from '@roe/shared';
import { BattleScene } from './BattleScene';
import { AuthService } from '../services/AuthService';
import { createHudTooltip } from '../utils/tooltip';
import { SyncService } from '../services/SyncService';
// SoundManager import removed — ui_click SFX disabled at all call sites.
// Re-import when re-enabling button-click audio.
// import { SoundManager } from '../effects/SoundManager';
import { attachPendingBadge, attachFriendOnlineToasts } from '../ui/SocialNotifications';
import { MultiplayerService } from '../services/MultiplayerService';
import { InviteToast } from '../ui/InviteToast';
import { SocialHub } from '../ui/SocialHub.js';
import { drawSocialIcon } from '../utils/glyphs';
import { FEATURE_MULTIPLAYER } from '../featureFlags.js';

// ── Drawn nav-grid glyphs (bronze/gold primitives — never emoji) ──────────────
// Each painter fills a ~22×22 icon centered at (cx, cy). Two-tone bronze palette
// matches the rest of the menu: `fg` = highlight stroke/fill, `bg` = dark accent.
type GridIconPainter = (gfx: Phaser.GameObjects.Graphics, cx: number, cy: number, fg: number, bg: number) => void;

const drawAccountIcon: GridIconPainter = (g, cx, cy, fg, bg) => {
  // Head (circle) + rounded shoulders (trapezoidal torso)
  g.fillStyle(fg, 1);
  g.fillCircle(cx, cy - 5, 4.5);
  g.fillTriangle(cx - 8, cy + 8, cx + 8, cy + 8, cx, cy - 1);
  g.fillRect(cx - 8, cy + 6, 16, 3);
  // Darker neck line for depth
  g.lineStyle(1, bg, 0.85);
  g.beginPath(); g.moveTo(cx - 2, cy - 1); g.lineTo(cx + 2, cy - 1); g.strokePath();
};

const drawScrollIcon: GridIconPainter = (g, cx, cy, fg, bg) => {
  // Parchment body + curled top/bottom ends
  g.fillStyle(fg, 1);
  g.fillRect(cx - 7, cy - 8, 14, 16);
  // Curled ends (darker ellipses)
  g.fillStyle(bg, 1);
  g.fillEllipse(cx, cy - 8, 14, 3);
  g.fillEllipse(cx, cy + 8, 14, 3);
  // Horizontal text lines
  g.lineStyle(1, bg, 0.9);
  g.beginPath();
  g.moveTo(cx - 4, cy - 3); g.lineTo(cx + 4, cy - 3);
  g.moveTo(cx - 4, cy);     g.lineTo(cx + 4, cy);
  g.moveTo(cx - 4, cy + 3); g.lineTo(cx + 2, cy + 3);
  g.strokePath();
};

const drawBookIcon: GridIconPainter = (g, cx, cy, fg, bg) => {
  // Open book — two pages fanning out from a central spine
  g.fillStyle(fg, 1);
  g.fillTriangle(cx - 9, cy + 7, cx - 9, cy - 6, cx - 1, cy - 7);
  g.fillTriangle(cx - 9, cy + 7, cx - 1, cy + 6, cx - 1, cy - 7);
  g.fillTriangle(cx + 9, cy + 7, cx + 9, cy - 6, cx + 1, cy - 7);
  g.fillTriangle(cx + 9, cy + 7, cx + 1, cy + 6, cx + 1, cy - 7);
  // Page spread lines
  g.lineStyle(1, bg, 0.7);
  g.beginPath();
  g.moveTo(cx - 7, cy - 4); g.lineTo(cx - 2, cy - 4);
  g.moveTo(cx - 7, cy);     g.lineTo(cx - 2, cy);
  g.moveTo(cx - 7, cy + 4); g.lineTo(cx - 2, cy + 4);
  g.moveTo(cx + 2, cy - 4); g.lineTo(cx + 7, cy - 4);
  g.moveTo(cx + 2, cy);     g.lineTo(cx + 7, cy);
  g.moveTo(cx + 2, cy + 4); g.lineTo(cx + 7, cy + 4);
  g.strokePath();
  // Spine
  g.lineStyle(1.5, bg, 0.95);
  g.beginPath(); g.moveTo(cx, cy - 7); g.lineTo(cx, cy + 6); g.strokePath();
};

const drawArmoryIcon: GridIconPainter = (g, cx, cy, fg, bg) => {
  // Anvil silhouette — heavy flat top, curved horn, square base. Reads as a
  // "forge / armory" mark matching the Cinzel/bronze language of the menu.
  g.fillStyle(fg, 1);
  g.fillRect(cx - 8, cy - 5, 14, 3);
  g.fillTriangle(cx + 6, cy - 5, cx + 10, cy - 4, cx + 6, cy - 2);
  g.fillRect(cx - 4, cy - 2, 6, 2);
  g.fillRect(cx - 7, cy, 12, 2);
  g.fillRect(cx - 6, cy + 2, 10, 4);
  // Accent underline
  g.lineStyle(1, bg, 0.8);
  g.beginPath(); g.moveTo(cx - 6, cy + 7); g.lineTo(cx + 4, cy + 7); g.strokePath();
};

const drawVersusIcon: GridIconPainter = (g, cx, cy, fg, bg) => {
  // Two crossed swords — a VS mark for multiplayer (simpler than two figures at this size)
  g.lineStyle(2, fg, 1);
  g.beginPath();
  g.moveTo(cx - 7, cy - 7); g.lineTo(cx + 6, cy + 6);
  g.moveTo(cx + 7, cy - 7); g.lineTo(cx - 6, cy + 6);
  g.strokePath();
  // Guards (crosspieces)
  g.lineStyle(1.5, bg, 1);
  g.beginPath();
  g.moveTo(cx - 8, cy - 4); g.lineTo(cx - 3, cy - 7);
  g.moveTo(cx + 8, cy - 4); g.lineTo(cx + 3, cy - 7);
  g.strokePath();
  // Pommels
  g.fillStyle(bg, 1);
  g.fillCircle(cx - 7, cy - 7, 1.5);
  g.fillCircle(cx + 7, cy - 7, 1.5);
};

export class MenuScene extends Phaser.Scene {
  private battleScene: (Phaser.Scene & { resumeBattle(): void; quitToMenu(): void }) | null = null;
  private gsm!: GameStateManager;
  /** Chat overlay — lazy-loaded. SocialHub onWhisperRequested invokes
   *  chatOverlay.openWhisper() so right-click-to-whisper routes through this. */
  private chatOverlay: import('../ui/ChatOverlay.js').ChatOverlay | null = null;
  private static _pageHideHandler: (() => void) | null = null;
  private static _visibilityHandler: (() => void) | null = null;

  /** Persisted music on/off state across scene restarts */
  static musicEnabled = true;
  /** Static BGM reference — persists across scene restarts so music doesn't restart */
  private static menuBgm: Phaser.Sound.BaseSound | null = null;

  constructor() {
    super({ key: 'MenuScene' });
  }

  init(data?: { gsm?: GameStateManager; battleScene?: any }): void {
    this.gsm = data?.gsm || GameStateManager.getInstance();
    this.battleScene = data?.battleScene || null;
  }

  create(): void {
    setupCamera(this, this.cameras.main, 'full');
    if (!this.gsm) this.gsm = GameStateManager.getInstance();
    // Refresh daily / weekly quest sets if their UTC reset window has elapsed.
    // Done here instead of in `init()` so the player sees the new quests right
    // away when they enter the lobby — there's no other natural surface where
    // the rollover would fire reliably (BattleScene uses init too, but the
    // player might never go back to the menu mid-session).
    this.gsm.maybeRollResets();

    // Chat overlay — anchored to the bottom of the main menu. Collapsed to a
    // small icon until the player presses Enter. We retain a reference on the
    // scene so the SocialHub's onWhisperRequested can call openWhisper().
    import('../ui/ChatOverlay.js').then(({ ChatOverlay }) => {
      if (!this.scene.isActive()) return;
      this.chatOverlay = new ChatOverlay(this, { anchor: 'bottom', sceneWidth: GAME_W, sceneHeight: GAME_H });
    }).catch((e) => console.warn('[chat] overlay load failed', e));

    // Wire save sync: GameStateManager.save() → SyncService.syncSave() when logged in
    GameStateManager.onSaveCallback = (slot, data, immediate) => SyncService.getInstance().syncSave(slot, data, immediate);
    // Wire login sync: init all game state from server, then restart scene to show full menu
    AuthService.onLoginSync = async () => {
      await GameStateManager.getInstance().init();
      this.scene.restart();
    };
    AuthService.onRegisterSync = null;

    // Flush saves when the tab is hidden or the page is closed.
    // Re-register on each create() to keep references current; remove old listeners first.
    if (MenuScene._pageHideHandler) {
      window.removeEventListener('beforeunload', MenuScene._pageHideHandler);
    }
    if (MenuScene._visibilityHandler) {
      window.removeEventListener('visibilitychange', MenuScene._visibilityHandler);
    }
    MenuScene._pageHideHandler = () => {
      SyncService.getInstance().flushPendingSave();
      GameStateManager.getInstance().flushAccountSync();
    };
    MenuScene._visibilityHandler = () => {
      if (document.visibilityState === 'hidden') MenuScene._pageHideHandler!();
    };
    window.addEventListener('visibilitychange', MenuScene._visibilityHandler);
    window.addEventListener('beforeunload', MenuScene._pageHideHandler);

    // setupCamera('full') above already resets viewport — no extra call needed

    // Stop BattleScene if arriving from Save & Quit (must happen here, after new MenuScene is rendering).
    // BattleScene is paused (not active) while the overlay menu is open, so check both states.
    const battleAlive = this.scene.isActive('BattleScene') || this.scene.isPaused('BattleScene');
    if (!this.battleScene && battleAlive) {
      this.scene.stop('BattleScene');
    }

    // Login gate: unauthenticated users see only the auth screen
    if (!AuthService.getInstance().isLoggedIn()) {
      this.createLoginGate();
      return;
    }

    // If logged in but data not yet loaded (e.g. page refresh), init first then restart
    if (!this.gsm.isInitialized) {
      this.showLoadingState();
      void this.gsm.init().then(() => this.scene.restart());
      return;
    }

    // F5 / tab-close detection: if an inferno run was active when the page reloaded, abandon it
    // and restore the pre-inferno hero snapshot so no items are kept from the abandoned run.
    if (this.gsm.player.infernoRunActive && !this.battleScene) {
      this.gsm.abandonInfernoRun();
      // Show a brief notice after the scene finishes drawing
      this.time.delayedCall(400, () => {
        this.showInfernoAbandonNotice();
      });
    }

    // F5 / tab-close during checkpoint death popup: player died with a checkpoint active but
    // hadn't yet chosen. Force-restore the checkpoint so they can't escape the penalty.
    if (this.gsm.player.checkpointDeathPending && !this.battleScene) {
      const savedScore = this.gsm.player.runeCheckpoint?.runScore ?? 0;
      this.gsm.restoreRunCheckpoint(); // restores state + clears pending flag
      this.gsm.clearRunCheckpoint();
      // Re-seed the run session so BattleScene resumes with the checkpoint score
      this.gsm.saveRunSession(0, savedScore, false, 0, 0);
      this.gsm.save(true);
    }

    if (this.battleScene) {
      this.createOverlayMenu();
    } else {
      this.createFullMenu();
      this.startMenuMusic();
    }
  }

  /** Shown when user is not logged in — only auth UI, no game content. */
  private createLoginGate(): void {
    this.cameras.main.setBackgroundColor('#0a0806');
    const W = 960;
    const H = 576;
    const cx = W / 2;

    this.add.text(cx, H / 2 - 70, 'Runes of Eternal', {
      fontSize: '38px', fontFamily: "'Cinzel Decorative', Georgia, serif", color: '#d4952a', fontStyle: 'bold',
      stroke: '#5a2e00', strokeThickness: 5,
    }).setOrigin(0.5);

    this.add.text(cx, H / 2 - 15, 'Login or register to play', {
      fontSize: '13px', fontFamily: 'Georgia, serif', color: '#5c4020',
    }).setOrigin(0.5);

    this.createMenuButton(cx, H / 2 + 35, '👤  Login', true, () => this.showAuthModal('login'));
    this.createMenuButton(cx, H / 2 + 80, '✦  Register', false, () => this.showAuthModal('register'));

    this.add.text(W - 12, H - 8, 'v6.0.0-alpha', {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: '#2d2018',
    }).setOrigin(1, 1);
  }

  /** Shown briefly while game data is being fetched from server after login. */
  private showLoadingState(): void {
    this.cameras.main.setBackgroundColor('#0a0806');
    const W = 960;
    const H = 576;

    this.add.text(W / 2, H / 2, 'Loading…', {
      fontSize: '18px', fontFamily: 'Georgia, serif', color: '#5c4020',
    }).setOrigin(0.5);
  }

  // ── Menu Music (static — persists across scene restarts & into HeroSelectScene) ──
  /** Update both the menu and battle BGM tracks to the latest
   *  `BattleScene.savedVolume`. Called from the volume slider so changes
   *  apply live, not just on next song-load. Either track may be null
   *  depending on which scene is actually playing; both are safe to skip. */
  private applyVolumeNow(): void {
    const v = BattleScene.savedVolume;
    // Persist to localStorage so the new volume survives a page reload.
    try { localStorage.setItem('roe_volume', String(v)); } catch { /* ignore */ }
    if (MenuScene.menuBgm) {
      try { (MenuScene.menuBgm as Phaser.Sound.WebAudioSound).volume = v; } catch { /* ignore */ }
    }
    // Reach into BattleScene's static `battleBgm` (typed private but readable
    // at runtime via cast). Updates any currently-playing battle BGM.
    const battleBgm = (BattleScene as unknown as { battleBgm: Phaser.Sound.BaseSound | null }).battleBgm;
    if (battleBgm) {
      try { (battleBgm as Phaser.Sound.WebAudioSound).volume = v; } catch { /* ignore */ }
    }
  }

  private startMenuMusic(): void {
    if (!MenuScene.musicEnabled) return;
    // Already playing — don't restart
    if (MenuScene.menuBgm && (MenuScene.menuBgm as any).isPlaying) return;
    if (!this.cache.audio.exists('bgm_menu')) return;
    // Destroy stale reference if it exists but isn't playing
    if (MenuScene.menuBgm) {
      MenuScene.menuBgm.destroy();
      MenuScene.menuBgm = null;
    }
    // Volume sourced from BattleScene.savedVolume so the ESC-menu slider
    // applies on first play. Was hardcoded 0.6 — slider had no effect on
    // the menu BGM after a fresh page load.
    MenuScene.menuBgm = this.sound.add('bgm_menu', { loop: true, volume: BattleScene.savedVolume });
    MenuScene.menuBgm.play();
  }

  /** Stop menu music globally (called when transitioning to battle) */
  static stopMenuMusic(): void {
    if (MenuScene.menuBgm) {
      MenuScene.menuBgm.stop();
      MenuScene.menuBgm.destroy();
      MenuScene.menuBgm = null;
    }
  }

  private stopMusic(): void {
    MenuScene.stopMenuMusic();
  }

  // ── Overlay mode: compact pause menu over battle ──
  private createOverlayMenu(): void {
    setupCamera(this, this.cameras.main, 'overlay');
    this.cameras.main.setBackgroundColor('rgba(10, 7, 4, 0.97)');

    // ESC closes overlay and resumes battle
    const canvas = this.game.canvas;
    canvas.setAttribute('tabindex', '1');
    canvas.focus();
    if (this.input.keyboard) {
      this.input.keyboard.on('keydown-ESC', () => {
        this.scene.stop();
        this.battleScene!.resumeBattle();
      });
    }
    this.input.on('pointerdown', () => canvas.focus());

    const W = 480;
    const H = 576;
    const cx = W / 2;

    // Title
    this.add.text(cx, 60, 'Runes of Eternal', {
      fontSize: '32px', fontFamily: "'Cinzel Decorative', Georgia, serif", color: '#c8973a', fontStyle: 'bold',
    }).setOrigin(0.5);

    this.add.text(cx, 105, 'PAUSED', {
      fontSize: '14px', fontFamily: 'Georgia, serif', color: '#8c7a62',
    }).setOrigin(0.5);

    // Player info
    const heroName = this.gsm.player.heroes[0]?.name || 'Hero';
    this.add.text(cx, 140, `${heroName} Lv${this.gsm.player.heroes[0]?.level || 1}  |  Zone ${this.gsm.player.currentLevel}`, {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#6b7280',
    }).setOrigin(0.5);

    // Resume button (main action)
    this.createMenuButton(cx, 200, '⚔  Return to Battle', true, () => {
      this.scene.stop();
      this.battleScene!.resumeBattle();
    });

    // Shop — renamed from "The Armory" now that the Hero Armory owns that label.
    this.createMenuButton(cx, 248, '⚒  The Shop', false, () => {
      this.scene.stop();
      this.scene.launch('ShopScene', { gsm: this.gsm, battleScene: this.battleScene, fromPauseMenu: true });
    });

    // ── Volume slider — moved here from the top-right HUD (the +/- buttons
    //    were stripped in the HUD reorg). Adjusts BattleScene.savedVolume
    //    directly so the new value persists across scene restarts and is
    //    picked up by the BGM / SFX subsystem on next play.
    // Per user feedback: more gap between "Music volume" label and the
    // [- 70% +] controls. Label anchored at cx-100, controls cluster
    // shifted right (track centre at cx+50 instead of cx+30) so there's a
    // ~24 px breathing gap between label end and the minus button.
    this.add.text(cx - 100, 296, 'Music volume', {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#a07840',
    }).setOrigin(0, 0.5);
    const trackCx = cx + 50;
    const volTrack = this.add.rectangle(trackCx, 296, 100, 18, 0x0d0a08, 0.95)
      .setStrokeStyle(1, 0x5a3600).setDepth(11);
    const updateVolLabel = (): void => {
      volPctTxt.setText(`${Math.round(BattleScene.savedVolume * 100)}%`);
    };
    const volMinusTxt = this.add.text(trackCx - 36, 296, '−', {
      fontSize: '16px', fontFamily: 'Georgia, serif', color: '#a07840', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(12).setInteractive({ useHandCursor: true });
    const volPctTxt = this.add.text(trackCx, 296, `${Math.round(BattleScene.savedVolume * 100)}%`, {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#c8973a',
    }).setOrigin(0.5).setDepth(12);
    const volPlusTxt = this.add.text(trackCx + 36, 296, '+', {
      fontSize: '16px', fontFamily: 'Georgia, serif', color: '#a07840', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(12).setInteractive({ useHandCursor: true });
    volMinusTxt.on('pointerover', () => volMinusTxt.setColor('#ffe8a0'));
    volMinusTxt.on('pointerout',  () => volMinusTxt.setColor('#a07840'));
    volPlusTxt.on('pointerover',  () => volPlusTxt.setColor('#ffe8a0'));
    volPlusTxt.on('pointerout',   () => volPlusTxt.setColor('#a07840'));
    volMinusTxt.on('pointerdown', () => {
      BattleScene.savedVolume = Math.max(0, Math.round((BattleScene.savedVolume - 0.1) * 10) / 10);
      this.applyVolumeNow();
      updateVolLabel();
    });
    volPlusTxt.on('pointerdown', () => {
      BattleScene.savedVolume = Math.min(1, Math.round((BattleScene.savedVolume + 0.1) * 10) / 10);
      this.applyVolumeNow();
      updateVolLabel();
    });
    void volTrack; // background reference, kept for layout; no further interaction

    // Achievements
    const unclaimedCount = this.gsm.getUnclaimedAchievementCount();
    const achLabel = unclaimedCount > 0 ? `Quests  (${unclaimedCount})` : 'Quests';
    this.createMenuButton(cx, 344, achLabel, false, () => {
      this.scene.stop();
      this.scene.launch('AchievementScene', { gsm: this.gsm, battleScene: this.battleScene, fromPauseMenu: true });
    });

    // Account
    const authOverlay = AuthService.getInstance();
    const acctLabel = authOverlay.isLoggedIn()
      ? '👤 My account'
      : 'Login / Register';
    this.createMenuButton(cx, 392, acctLabel, false, () => {
      if (authOverlay.isLoggedIn()) this.showAccountMenu();
      else this.showAuthModal('login');
    });

    // Save & Quit — mirrors defeat popup MENU button exactly (save + stop music + stop battle + start menu)
    this.createMenuButton(cx, 440, '✦  Save & Return to Menu', false, () => {
      this.gsm.save();
      BattleScene.stopBattleMusic();
      this.scene.stop('BattleScene');      // stop battle scene directly by key
      this.scene.start('MenuScene', {});   // restart MenuScene with no data → init({}) → battleScene=null → full menu
    });

    // Close button
    this.add.text(W - 24, 20, 'X', {
      fontSize: '18px', fontFamily: 'Georgia, serif', color: '#ef4444',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true }).on('pointerdown', () => {
      this.scene.stop();
      this.battleScene!.resumeBattle();
    });

    // Version
    this.add.text(cx, H - 20, 'v6.0.0-alpha', {
      fontSize: '10px', fontFamily: 'Georgia, serif', color: '#374151',
    }).setOrigin(0.5);
  }

  // ── Full-screen mode: main menu at game start ──
  private createFullMenu(): void {
    this.cameras.main.setBackgroundColor('#0a0806');
    const W = 960;
    const H = 576;
    const cx = W / 2;

    // ── Enchanted stones — single full-screen backdrop ──
    if (this.textures.exists('menu_bg_enchanted')) {
      this.add.image(cx, H / 2, 'menu_bg_enchanted')
        .setDisplaySize(W, H)
        .setOrigin(0.5, 0.5)
        .setAlpha(1.0);
    }
    // Soft top gradient — darkens the top edge so the title reads cleanly against the sky
    const grad = this.add.graphics();
    for (let i = 0; i < 140; i++) {
      const a = Math.pow(1 - i / 140, 1.6) * 0.70;
      grad.fillStyle(0x0a0806, a);
      grad.fillRect(0, i, W, 1);
    }

    // ── Title — moved to BOTTOM-LEFT and halved in size per user direction.
    // The lobby reads as a workspace now (deck of saves + tool tiles), not a
    // splash screen, so the wordmark only needs to label the room rather than
    // dominate it. Drop shadow + main + highlight layers preserved at half
    // scale; the old centered ornamental dividers and "DARKNESS RISES…"
    // subtitle were dropped (they belong to a splash, not a room corner).
    const titleX = 18;
    const titleY = H - 18;
    const titleText = 'Runes of Eternal';
    const titleSize = '21px';
    // Drop shadow
    this.add.text(titleX + 2, titleY + 3, titleText, {
      fontSize: titleSize, fontFamily: "'Cinzel Decorative', Georgia, serif", color: '#000000', fontStyle: 'bold',
      stroke: '#000000', strokeThickness: 4,
    }).setOrigin(0, 1).setAlpha(0.55);
    // Main gold text
    this.add.text(titleX, titleY, titleText, {
      fontSize: titleSize, fontFamily: "'Cinzel Decorative', Georgia, serif", color: '#d4952a', fontStyle: 'bold',
      stroke: '#5a2e00', strokeThickness: 3,
      shadow: { offsetX: 1, offsetY: 2, color: '#1a0a00', blur: 3, fill: true, stroke: false },
    }).setOrigin(0, 1);
    // Highlight shimmer
    this.add.text(titleX, titleY - 1, titleText, {
      fontSize: titleSize, fontFamily: "'Cinzel Decorative', Georgia, serif", color: '#ffe8a0', fontStyle: 'bold',
    }).setOrigin(0, 1).setAlpha(0.18);

    // ── Top-right header cluster: [music icon] [Social Hub] ──
    // Social on the rightmost edge, music icon-only just to its left per user
    // direction. Both gated behind FEATURE_MULTIPLAYER for the Steam build —
    // the Social hub is purely a multiplayer surface (friends, invites,
    // whispers, online status), and the music toggle stays universal but
    // moves regardless of the social presence so the layout reads cleanly.
    if (FEATURE_MULTIPLAYER) {
      // Social hub — keeps icon + "Social" label, anchored to the right edge.
      const SOC_W = 70;
      const SOC_H = 22;
      const SOC_CX = W - SOC_W / 2 - 12; // 12 px right margin
      const SOC_CY = 18;
      const socialBg = this.add.rectangle(SOC_CX, SOC_CY, SOC_W, SOC_H, 0x0d0a08, 0.95)
        .setStrokeStyle(1, 0x5a3600).setDepth(10).setInteractive({ useHandCursor: true });
      const socialIconGfx = this.add.graphics().setDepth(11);
      drawSocialIcon(socialIconGfx, SOC_CX - 22, SOC_CY, 0xc8973a);
      const socialLabel = this.add.text(SOC_CX + 4, SOC_CY, 'Social', {
        fontSize: '11px', fontFamily: 'Georgia, serif', color: '#c8973a',
      }).setOrigin(0.5).setDepth(11).setInteractive({ useHandCursor: true });
      const socialOver = () => {
        socialBg.setStrokeStyle(1, 0xc8973a);
        socialLabel.setColor('#e8b75a');
      };
      const socialOut = () => {
        socialBg.setStrokeStyle(1, 0x5a3600);
        socialLabel.setColor('#c8973a');
      };
      const openSocialMenu = () => {
        // Static import (see BattleScene.openSocial) — avoids chunk-fetch
        // failures when a stale index-*.js session references a now-removed
        // SocialHub-*.js hash.
        const hub: { close?: () => void } = { close: undefined };
        const instance = new SocialHub(this, {
          mode: 'fullscreen', sceneWidth: GAME_W, sceneHeight: GAME_H,
          onClose: () => { hub.close = undefined; },
          onWhisperRequested: (userId, name) => {
            this.chatOverlay?.openWhisper(userId, name);
          },
        });
        hub.close = () => instance.close();
      };
      socialBg.on('pointerover', socialOver).on('pointerout', socialOut).on('pointerdown', openSocialMenu);
      socialLabel.on('pointerover', socialOver).on('pointerout', socialOut).on('pointerdown', openSocialMenu);

      // Pending-request pip on the social button + friend-online popups while
      // the main menu is visible. Both self-clean on scene shutdown/destroy.
      // Anchored OUTSIDE the button's hitbox (top-right corner just above the bg) so it
      // can't interfere with clicks.
      attachPendingBadge(this, { x: SOC_CX + SOC_W / 2 - 4, y: SOC_CY - SOC_H / 2 });
      attachFriendOnlineToasts(this);
    }
    // Invite toast — bound at every scene that should surface incoming
    // multiplayer invitations. Without this, a player sitting on the main
    // menu would never see a friend's invite (the previous handler lived
    // only in MultiplayerLobbyBase). Cleared on scene shutdown.
    // Gated behind FEATURE_MULTIPLAYER for the Steam build (flag = false).
    if (FEATURE_MULTIPLAYER) {
      const inviteHandler = (payload: { inviteId: string; fromUserId: string; fromName: string; mode: 'pvp' | 'coop'; expiresAt: number }): void => {
        InviteToast.getInstance().showInvite(this, payload);
      };
      MultiplayerService.getInstance().bindInviteToast(inviteHandler);
      this.events.once('shutdown', () => MultiplayerService.getInstance().unbindInviteToast(inviteHandler));
      this.events.once('destroy', () => MultiplayerService.getInstance().unbindInviteToast(inviteHandler));
    }

    // Music toggle — icon only (no "Music ON/OFF" text label) per user
    // direction. Sits immediately left of the Social hub at top-right.
    // Glyph: ♫ when on, ♩ when off (single quarter-note reads as "muted /
    // quiet" without needing extra slash-out artwork).
    const MUSIC_W = 22;
    const MUSIC_H = 22;
    const MUSIC_CX = (FEATURE_MULTIPLAYER ? (W - 70 - 12 - MUSIC_W / 2 - 8) : (W - MUSIC_W / 2 - 12));
    const MUSIC_CY = 18;
    const musicBg = this.add.rectangle(MUSIC_CX, MUSIC_CY, MUSIC_W, MUSIC_H, 0x0d0a08, 0.95)
      .setStrokeStyle(1, 0x5a3600).setDepth(10).setInteractive({ useHandCursor: true });
    const musicGlyph = this.add.text(MUSIC_CX, MUSIC_CY, MenuScene.musicEnabled ? '♫' : '♩', {
      fontSize: '14px', fontFamily: 'Georgia, serif',
      color: MenuScene.musicEnabled ? '#c8973a' : '#6b7280', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(11);
    const setMusicVisuals = (on: boolean): void => {
      musicGlyph.setText(on ? '♫' : '♩');
      musicGlyph.setColor(on ? '#c8973a' : '#6b7280');
    };
    musicBg.on('pointerover', () => {
      musicBg.setStrokeStyle(1, 0xc8973a);
      musicGlyph.setColor(MenuScene.musicEnabled ? '#e8b75a' : '#9ca3af');
    });
    musicBg.on('pointerout', () => {
      musicBg.setStrokeStyle(1, 0x5a3600);
      setMusicVisuals(MenuScene.musicEnabled);
    });
    musicBg.on('pointerdown', () => {
      MenuScene.musicEnabled = !MenuScene.musicEnabled;
      setMusicVisuals(MenuScene.musicEnabled);
      if (MenuScene.musicEnabled) this.startMenuMusic();
      else this.stopMusic();
    });

    // ── Fullscreen toggle — sits LEFT of the music icon at top-right.
    // Calls Phaser's scale.startFullscreen() (JS Fullscreen API path) so
    // the canvas takes the whole screen without browser chrome eating
    // pixels — the windowed view still uses the responsive separate-X/Y
    // canvas, but fullscreen is the recommended way to play until we find
    // a non-stretching look that the user is happy with in windowed mode.
    // Icon: four outward-pointing L-brackets (Phaser-drawn, not emoji),
    // flipped to inward when already in fullscreen. Drawn via Graphics so
    // the no-emoji-in-UI rule stays clean.
    const FS_W = 22;
    const FS_H = 22;
    const FS_CX = MUSIC_CX - MUSIC_W / 2 - 8 - FS_W / 2;
    const FS_CY = 18;
    const fsBg = this.add.rectangle(FS_CX, FS_CY, FS_W, FS_H, 0x0d0a08, 0.95)
      .setStrokeStyle(1, 0x5a3600).setDepth(10).setInteractive({ useHandCursor: true });
    const fsGfx = this.add.graphics().setDepth(11);
    const drawFsIcon = (color: number, expanded: boolean): void => {
      fsGfx.clear();
      fsGfx.lineStyle(1.5, color, 1);
      if (!expanded) {
        // outward L-brackets (enter fullscreen)
        fsGfx.lineBetween(FS_CX - 6, FS_CY - 1, FS_CX - 6, FS_CY - 6); fsGfx.lineBetween(FS_CX - 6, FS_CY - 6, FS_CX - 1, FS_CY - 6);
        fsGfx.lineBetween(FS_CX + 1, FS_CY - 6, FS_CX + 6, FS_CY - 6); fsGfx.lineBetween(FS_CX + 6, FS_CY - 6, FS_CX + 6, FS_CY - 1);
        fsGfx.lineBetween(FS_CX - 6, FS_CY + 1, FS_CX - 6, FS_CY + 6); fsGfx.lineBetween(FS_CX - 6, FS_CY + 6, FS_CX - 1, FS_CY + 6);
        fsGfx.lineBetween(FS_CX + 1, FS_CY + 6, FS_CX + 6, FS_CY + 6); fsGfx.lineBetween(FS_CX + 6, FS_CY + 6, FS_CX + 6, FS_CY + 1);
      } else {
        // inward L-brackets (exit fullscreen)
        fsGfx.lineBetween(FS_CX - 6, FS_CY - 1, FS_CX - 1, FS_CY - 1); fsGfx.lineBetween(FS_CX - 1, FS_CY - 1, FS_CX - 1, FS_CY - 6);
        fsGfx.lineBetween(FS_CX + 6, FS_CY - 1, FS_CX + 1, FS_CY - 1); fsGfx.lineBetween(FS_CX + 1, FS_CY - 1, FS_CX + 1, FS_CY - 6);
        fsGfx.lineBetween(FS_CX - 6, FS_CY + 1, FS_CX - 1, FS_CY + 1); fsGfx.lineBetween(FS_CX - 1, FS_CY + 1, FS_CX - 1, FS_CY + 6);
        fsGfx.lineBetween(FS_CX + 6, FS_CY + 1, FS_CX + 1, FS_CY + 1); fsGfx.lineBetween(FS_CX + 1, FS_CY + 1, FS_CX + 1, FS_CY + 6);
      }
    };
    drawFsIcon(0xc8973a, this.scale.isFullscreen);
    fsBg.on('pointerover', () => {
      fsBg.setStrokeStyle(1, 0xc8973a);
      drawFsIcon(0xe8b75a, this.scale.isFullscreen);
    });
    fsBg.on('pointerout', () => {
      fsBg.setStrokeStyle(1, 0x5a3600);
      drawFsIcon(0xc8973a, this.scale.isFullscreen);
    });
    fsBg.on('pointerdown', () => {
      if (this.scale.isFullscreen) this.scale.stopFullscreen();
      else this.scale.startFullscreen();
    });
    this.scale.on('enterfullscreen', () => drawFsIcon(0xc8973a, true));
    this.scale.on('leavefullscreen', () => drawFsIcon(0xc8973a, false));
    this.events.once('shutdown', () => {
      this.scale.off('enterfullscreen');
      this.scale.off('leavefullscreen');
    });

    // ── Account Runes display — TOP-CENTER per user direction. Sits between
    // the top-left nav row and the top-right music/social cluster as the
    // "currency" anchor of the lobby. Rune count is now centered inside
    // the box (was left-anchored to leave room for a "+" → RuneShop
    // entry point that has been removed per user direction). Box width
    // tightened to match the new content width. `RuneShopScene` itself
    // is preserved in the codebase so it can be reopened later, just no
    // longer reachable from the lobby UI.
    const runeBoxX = cx;
    const runeBoxY = 18;
    const runeBg = this.add.rectangle(runeBoxX, runeBoxY, 76, 28, 0x0d0a08, 0.95)
      .setStrokeStyle(1, 0x5a3600).setDepth(10).setInteractive({ useHandCursor: false });
    this.add.text(runeBoxX, runeBoxY, `◆ ${this.gsm.player.resources.loot}`, {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#a07840',
      stroke: '#2a1004', strokeThickness: 3,
    }).setOrigin(0.5, 0.5).setDepth(11);
    // Hover tooltip — was a complete mystery to new players (just a number
    // with a diamond glyph). Spell out where runes come from and what
    // they're for. Sigil Vendor entry point lives in the inventory page;
    // the lobby itself doesn't link to it any more (per the recent
    // "remove the +" pass) so the tooltip is the canonical surface.
    let runeTip: Phaser.GameObjects.Text | null = null;
    runeBg.on('pointerover', () => {
      runeTip?.destroy();
      runeTip = this.add.text(runeBoxX, runeBoxY + 22,
        '◆ Runes\nAccount-wide currency.\nEarned from runs & quests.\nSpend on Sigil Vendor / sigil insurance.', {
          fontSize: '9px', fontFamily: 'Georgia, serif', color: '#c8973a',
          backgroundColor: '#0d0a08', padding: { x: 8, y: 6 }, lineSpacing: 3,
          align: 'center',
        }).setOrigin(0.5, 0).setDepth(55).setResolution(2);
    });
    runeBg.on('pointerout', () => { runeTip?.destroy(); runeTip = null; });

    // ── Top-LEFT vertical nav column: Account · Multiplayer · Codex ──
    // Stacked vertically per user direction so the lobby's left edge reads
    // as a slim toolbar rather than a row competing with the title strip.
    // Tile size kept smaller than the bottom-right action row so the user
    // glance hierarchy stays "saves first, primary actions next, side
    // navigation last". `TOP_NAV_Y0 = 64` gives a clear gap from the top.
    const authInst = AuthService.getInstance();
    const isLoggedIn = authInst.isLoggedIn();
    const TOP_NAV_X    = 36;
    const TOP_NAV_Y0   = 64;
    const TOP_NAV_STEP = 60; // 42-px tile + label below + spacing
    const TOP_NAV_SIZE = 42;
    const topNavCells: { draw: GridIconPainter; label: string; onClick: () => void; disabled?: boolean; disabledTooltip?: string }[] = [
      { draw: drawAccountIcon, label: isLoggedIn ? 'Account' : 'Login',
        onClick: () => (isLoggedIn ? this.showAccountMenu() : this.showAuthModal('login')) },
    ];
    if (FEATURE_MULTIPLAYER) {
      // Multiplayer is feature-flagged ON for web builds, but the mode itself
      // isn't shippable yet — keep the tile visible so players know it's
      // coming, but force disabled so no popup / mode picker can open.
      topNavCells.push({
        draw: drawVersusIcon, label: 'Multiplayer',
        onClick: () => { /* disabled — not accessible until MP is shippable */ },
        disabled: true,
        disabledTooltip: 'Coming soon',
      });
    }
    topNavCells.push({
      draw: drawBookIcon, label: 'Codex',
      onClick: () => { this.stopMusic(); this.scene.start('CodexScene'); },
    });
    topNavCells.forEach((c, i) => {
      this.createGridIconButton(
        TOP_NAV_X, TOP_NAV_Y0 + i * TOP_NAV_STEP,
        c.draw, c.label, c.onClick, undefined, c.disabled, c.disabledTooltip,
        TOP_NAV_SIZE,
      );
    });

    // ── Save Slot layout — ACTIVE card centered, others in a side LIST ──
    // Per user direction the previous deck-overlap was unreadable; the new
    // pattern is "headline + tabs": the active save renders as a full card
    // at center stage, every other save sits in a vertical list on the
    // right edge (compact mini-tile per slot), and the [+] New Save tile
    // is the last entry of that list. Clicking a side mini-tile switches
    // the slot, which on re-render makes that slot the new center card —
    // i.e. the clicked save "pops" to the middle.
    const CARD_W = 152;
    const CARD_H = 132;
    const CARD_Y = 197;
    // Side list — vertical column hugging the right edge, between the top
    // music/social cluster and the bottom-right tile row. The [+] New Save
    // and [+] New Inferno tiles share the same SIDE_W / SIDE_H footprint
    // so the column reads uniformly.
    const SIDE_W = 132;
    const SIDE_H = 40;
    const SIDE_GAP = 6;
    const SIDE_X = W - SIDE_W / 2 - 14;
    const SIDE_Y0 = 110;

    const currentSlot = this.gsm.player.saveSlot;

    // Collect all slots that have data or are the current active slot
    const usedSlots: number[] = [];
    for (let si = 1; si <= 5; si++) {
      const inf = this.gsm.getSaveSlotInfo(si);
      if ((inf && (inf.setupComplete || inf.heroes > 0)) || si === currentSlot) {
        if (!usedSlots.includes(si)) usedSlots.push(si);
      }
    }
    usedSlots.sort((a, b) => a - b);

    // Can add a new slot only if every existing slot already has a hero AND total < 5
    const allHaveHeroes = usedSlots.every(si => {
      const inf = this.gsm.getSaveSlotInfo(si);
      return inf && (inf.setupComplete || inf.heroes > 0);
    });
    const canAddSlot = usedSlots.length < 5 && allHaveHeroes;

    // No deck math needed — the active card sits at (cx, CARD_Y) as a single
    // hero card, and the side list is anchored to the right column below.

    // Helper — draws corner accent marks on a card
    const drawCorners = (gfx: Phaser.GameObjects.Graphics, cx2: number, cy2: number, cw: number, ch: number) => {
      const cl = 8;
      const hw = cw / 2 - 4;
      const hh = ch / 2 - 4;
      gfx.strokePoints([{x:cx2-hw,y:cy2-hh+cl},{x:cx2-hw,y:cy2-hh},{x:cx2-hw+cl,y:cy2-hh}],false);
      gfx.strokePoints([{x:cx2+hw-cl,y:cy2-hh},{x:cx2+hw,y:cy2-hh},{x:cx2+hw,y:cy2-hh+cl}],false);
      gfx.strokePoints([{x:cx2-hw,y:cy2+hh-cl},{x:cx2-hw,y:cy2+hh},{x:cx2-hw+cl,y:cy2+hh}],false);
      gfx.strokePoints([{x:cx2+hw-cl,y:cy2+hh},{x:cx2+hw,y:cy2+hh},{x:cx2+hw,y:cy2+hh-cl}],false);
    };

    // Small rune chip drawn at the top-center of a save-slot card showing how many sigils
    // (1–3) are currently equipped for that slot. Uses the same golden-rune glyph (◆) the
    // rune-currency tally uses so the icon language stays consistent. Hidden when none equipped.
    const drawSigilBadge = (cardX2: number, ctop: number, slot: number): void => {
      const count = this.gsm.equippedSigilCountForSlot(slot);
      if (count <= 0) return;
      const badgeW = 22, badgeH = 14;
      const by = ctop + 8;
      this.add.rectangle(cardX2, by, badgeW, badgeH, 0x0d0a0e, 0.92)
        .setStrokeStyle(1, 0xc8973a).setDepth(0.6);
      this.add.text(cardX2 - 6, by, '◆', {
        fontSize: '10px', fontFamily: 'Georgia, serif', color: '#f0c040', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(0.7);
      this.add.text(cardX2 + 6, by, `${count}`, {
        fontSize: '9px', fontFamily: 'monospace', color: '#f0c040', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(0.7);
    };

    // Render the ACTIVE slot only, as a full-size card centred at (cx, CARD_Y).
    // Other slots are rendered below as compact side-list mini-tiles. The
    // existing render code (inferno + normal branches, portraits, sigil badge
    // etc.) is reused unchanged for the centre card by force-feeding it
    // cardX2 = cx and isAct = true.
    // The centre card always renders. Default to the most recently played
    // slot (currentSlot — saved as the active one). If currentSlot isn't in
    // usedSlots (e.g. just deleted, or a fresh launch with no saves yet),
    // fall back to the first existing save; if no saves exist at all, show
    // an empty placeholder card on slot 1 so the lobby never reads as
    // "nothing here". Side list shows ALL slots (including the centre
    // slot) so the picked slot stays visible on the right after selection.
    //
    // KEY: the centre card IS "the active save" visually — the user
    // expects the action buttons (Enter the Battlefield, etc.) to operate
    // on whatever's in the middle without an extra click. So when our
    // displaySlot differs from the GSM's recorded currentSlot, auto-
    // activate it via switchSaveSlot and restart the scene so every
    // downstream read (`slotSelected`, `hasActiveHero`, the run-context
    // text, the Delete Save button, etc.) keys off the right state.
    let displaySlot: number;
    if (usedSlots.includes(currentSlot) && currentSlot >= 1) {
      displaySlot = currentSlot;
    } else if (usedSlots.length > 0) {
      displaySlot = usedSlots[0];
      if (this.gsm.player.saveSlot !== displaySlot) {
        this.gsm.switchSaveSlot(displaySlot);
        this.scene.restart();
        return;
      }
    } else {
      displaySlot = 1;
    }
    const centreSlots = [displaySlot];
    centreSlots.forEach((slot) => {
      const isAct  = true;
      const cardX2 = cx;
      const cardY2 = CARD_Y;
      const ctop   = cardY2 - CARD_H / 2;
      const cbot   = cardY2 + CARD_H / 2;
      const info   = this.gsm.getSaveSlotInfo(slot);
      const hasH   = !!(info && (info.setupComplete || info.heroes > 0));

      const isInfernoSlot = !!(info?.infernoOnly);

      if (isInfernoSlot && info) {
        // ── INFERNO CARD — badass dark-red fire design ──────────────────────
        // Ember glow behind card
        if (isAct) this.add.rectangle(cardX2, cardY2, CARD_W + 8, CARD_H + 8, 0xcc2200, 0.10);
        else        this.add.rectangle(cardX2, cardY2, CARD_W + 6, CARD_H + 6, 0x8b1000, 0.06);

        // Card body — near-black with red tint
        const infBg = this.add.rectangle(cardX2, cardY2, CARD_W, CARD_H, 0x0e0000, 0.98)
          .setStrokeStyle(isAct ? 2 : 1, isAct ? 0xcc2200 : 0x6b1000, isAct ? 1 : 0.7);
        this.add.rectangle(cardX2, cardY2, CARD_W - 8, CARD_H - 8, 0, 0)
          .setStrokeStyle(1, isAct ? 0xff4020 : 0x3a0a00, isAct ? 0.3 : 0.15);

        // Subtle ember line along the bottom edge (replaces heavy gradient)
        this.add.rectangle(cardX2, cbot - 3, CARD_W - 2, 6, 0xff4800, 0.22).setDepth(0.3);
        this.add.rectangle(cardX2, cbot - 1, CARD_W - 2, 2, 0xff7000, 0.18).setDepth(0.3);

        // Small flame tips along bottom edge
        const fgC = this.add.graphics().setDepth(0.5);
        for (let fi = -3; fi <= 3; fi++) {
          const fx3 = cardX2 + fi * 18; const fy3 = cbot;
          const fh3 = 10 + Math.abs(fi % 2) * 4;
          fgC.fillStyle(0x8b2800, 0.85); fgC.fillTriangle(fx3, fy3 - fh3, fx3 - 6, fy3, fx3 + 6, fy3);
          fgC.fillStyle(0xcc4010, 0.6);  fgC.fillTriangle(fx3, fy3 - (fh3 - 3), fx3 - 3, fy3, fx3 + 3, fy3);
        }

        // Corner accent marks in red
        const cgInf = this.add.graphics();
        cgInf.lineStyle(1, isAct ? 0xcc2200 : 0x6b1000, isAct ? 0.8 : 0.5);
        drawCorners(cgInf, cardX2, cardY2, CARD_W, CARD_H);

        // Slot number + Inferno label removed — keep top of card clean

        // 3 hero portraits — 2nd hero (left, mirrored), main (center), 3rd (right)
        const heroData = info.infernoHeroData ?? [];
        const portraitSlots: { x: number; data: typeof heroData[0] | undefined; flip: boolean }[] = [
          { x: cardX2 - 36, data: heroData[1], flip: true  },
          { x: cardX2,      data: heroData[0], flip: false },
          { x: cardX2 + 36, data: heroData[2], flip: false },
        ];

        // ── Inferno backdrop — crypt scene with heavy red recolor, fills INNER frame (aspect preserved) ──
        const plateW = CARD_W - 8;
        const plateH = CARD_H - 8;
        const plateY = cardY2;
        if (this.textures.exists('save_bg_crypt')) {
          // Uniform "cover" scale + centered crop — same approach as normal card, keeps aspect correct
          const tex = this.textures.get('save_bg_crypt');
          const srcW = tex.source[0]?.width ?? 1920;
          const srcH = tex.source[0]?.height ?? 1080;
          const coverScale = Math.max(plateW / srcW, plateH / srcH);
          const cropW = plateW / coverScale;
          const cropH = plateH / coverScale;
          const cropX = (srcW - cropW) / 2;
          const cropY = (srcH - cropH) / 2;
          this.add.image(cardX2, plateY, 'save_bg_crypt')
            .setDepth(0.25).setAlpha(isAct ? 1 : 0.85)
            .setScale(coverScale)
            .setCrop(cropX, cropY, cropW, cropH)
            .setTint(isAct ? 0xcc2200 : 0x701000); // heavy red for active, deep dark red for inactive
        }
        // Inset red highlight line — inside the outer wooden frame
        this.add.rectangle(cardX2, plateY, plateW - 6, plateH - 6, 0, 0)
          .setStrokeStyle(1, isAct ? 0xcc2200 : 0x6b1000, 0.85).setDepth(0.3);

        portraitSlots.forEach(({ x: px, data: pd, flip }, pi) => {
          if (!pd) return;
          const pSize = pi === 1 ? 80 : 48;
          const py    = cardY2 - 10 + (pi === 1 ? 0 : 4);
          const pKeyC = [pd.heroKey, pd.heroClass].filter((k): k is string => !!k)
            .find((k) => this.textures.exists(`hero_${k}`));
          if (pKeyC) {
            // Side-portraits are shown free-standing (no circular frame behind them) —
            // matches the normal save-slot card style.
            // Pixellab hero silhouettes taper into thin toe-tips, so the shadow needs to
            // lift up under the visible body mass. Offsets scale with pSize so the main-hero
            // portrait (80px) and roster side-portraits (48px) both land right.
            const SHADOW_LIFT: Record<string, number> = { necromancer: -0.05, shaman: -0.05, druid: -0.05 };
            const liftFrac = SHADOW_LIFT[pKeyC] ?? 0;
            // Cyrith's sorcerer sheet has the character art baked into the left third
            // of each 128px frame, so a centered-on-px sprite reads as shifted-left.
            // Nudge in source pixels, scaled by displayed pSize, mirrored when flipped.
            const PORTRAIT_X_NUDGE: Record<string, number> = { sorcerer: 24 };
            const xNudge = Math.round((PORTRAIT_X_NUDGE[pKeyC] ?? 0) * (pSize / 128)) * (flip ? -1 : 1);
            this.add.ellipse(px + xNudge, py + pSize / 2 + 2 + Math.round(pSize * liftFrac), pSize * 0.55, 4, 0x000000, 0.5).setDepth(0.4);
            const spr = this.add.sprite(px + xNudge, py, `hero_${pKeyC}`)
              .setDisplaySize(pSize, pSize).setOrigin(0.5, 0.5).setDepth(1 + pi * 0.1)
              .setTint(0xb05030).setFlipX(flip);
            const tex2 = this.textures.get(`hero_${pKeyC}`);
            if (tex2 && tex2.source[0]) tex2.setFilter(Phaser.Textures.FilterMode.LINEAR);
            const idleK = `hero_${pKeyC}_idle`;
            if (this.anims.exists(idleK)) spr.play(idleK);
          }
          if (pi !== 1) {
            this.add.text(px, py + pSize / 2 + 8, pd.name?.split(' ')[0] ?? '', {
              fontSize: '7px', fontFamily: 'Georgia, serif', color: '#8b3a18',
              stroke: '#000', strokeThickness: 2,
            }).setOrigin(0.5, 0.5).setDepth(2);
          }
        });

        // Main hero name — sits directly on the backdrop with thick stroke for readability
        const mainHeroData = heroData[0];
        if (mainHeroData) {
          this.add.text(cardX2, cbot - 20, mainHeroData.name?.split(' ')[0] ?? 'Hero', {
            fontSize: '12px', fontFamily: "'Cinzel Decorative', 'Palatino Linotype', Georgia, serif",
            color: isAct ? '#ffa070' : '#c85030', fontStyle: 'bold',
            stroke: '#000', strokeThickness: 4, shadow: { offsetX: 0, offsetY: 1, color: '#000', blur: 3, fill: true },
          }).setOrigin(0.5, 0.5).setDepth(3);
        }

        // Status line
        const statusLabel = info.heroLevel <= 1 ? '☠  FALLEN' : `☠  Lv ${info.heroLevel}`;
        this.add.text(cardX2, cbot - 8, statusLabel, {
          fontSize: '10px', fontFamily: 'Georgia, serif',
          color: info.heroLevel <= 1 ? '#ef4444' : '#ff7050', fontStyle: 'bold',
          stroke: '#000', strokeThickness: 3, shadow: { offsetX: 0, offsetY: 1, color: '#000', blur: 2, fill: true },
        }).setOrigin(0.5, 0.5).setDepth(3);

        // Inactive card: clickable to switch slot
        if (!isAct) {
          infBg.setInteractive({ useHandCursor: true });
          infBg.on('pointerover',  () => { infBg.setFillStyle(0x1a0000, 0.98); infBg.setStrokeStyle(1, 0xcc2200, 0.5); });
          infBg.on('pointerout',   () => { infBg.setFillStyle(0x0e0000, 0.98); infBg.setStrokeStyle(1, 0x6b1000, 0.7); });
          infBg.on('pointerdown', () => {
            this.time.removeAllEvents(); this.tweens.killAll();
            this.gsm.switchSaveSlot(slot);
            this.scene.restart();
          });
        }

      } else {
        // ── NORMAL CARD ─────────────────────────────────────────────────────

        // Glow behind active card
        if (isAct) this.add.rectangle(cardX2, cardY2, CARD_W + 6, CARD_H + 6, 0xc8973a, 0.07);

        // Card body
        const cardBg = this.add.rectangle(cardX2, cardY2, CARD_W, CARD_H,
          isAct ? 0x2a1a0a : 0x140e06, 0.97)
          .setStrokeStyle(isAct ? 2 : 1, isAct ? 0xc8973a : 0x3a2010, isAct ? 0.9 : 0.55);

        // Inset frame
        this.add.rectangle(cardX2, cardY2, CARD_W - 8, CARD_H - 8, 0, 0)
          .setStrokeStyle(1, isAct ? 0xc8973a : 0x2a1808, isAct ? 0.14 : 0.07);

        // Corner marks on active card
        if (isAct) {
          const cg2 = this.add.graphics();
          cg2.lineStyle(1, 0xd4a855, 0.7);
          drawCorners(cg2, cardX2, cardY2, CARD_W, CARD_H);
        }

        if (hasH && info) {
          // Zone comes from the shared battle logic so the card matches the real
          // in-game biome (including Ashen Labyrinth substitution at lvl 38+).
          const zone = getZoneForLevel(info.level);

          // Hero portraits — same layout as inferno: 2nd left/flipped, main center, 3rd right
          const heroData = info.heroData ?? [];
          const portraitSlots: { x: number; data: typeof heroData[0] | undefined; flip: boolean }[] = [
            { x: cardX2 - 36, data: heroData[1], flip: true  },
            { x: cardX2,      data: heroData[0], flip: false },
            { x: cardX2 + 36, data: heroData[2], flip: false },
          ];
          // ── Biome-specific backdrop — fills the INNER frame area (inside the wooden border, aspect preserved) ──
          const plateW = CARD_W - 8;   // = 144, matches inner-frame rect, leaves the wooden border visible
          const plateH = CARD_H - 8;   // = 124
          const plateY = cardY2;
          // Map zone themeKey → in-game battle background key so the card matches
          // what the player will actually see on the battlefield.
          const biomeBgMap: Record<string, string> = {
            zone_forest:    'zone_background_forest',
            zone_crypt:     'zone_background_crypt',
            zone_wastes:    'zone_background_wastes',
            zone_rift:      'zone_background_rift',
            zone_shadow:    'zone_background_shadow',
            zone_labyrinth: 'zone_background_labyrinth',
          };
          const biomeKey = biomeBgMap[zone.themeKey] ?? 'zone_background_forest';
          if (this.textures.exists(biomeKey)) {
            // Uniform "cover" scale + centered crop so the biome image fills the plate
            // without being flattened. setDisplaySize uses different scaleX/scaleY which stretched it;
            // setScale + setCrop keeps aspect correct.
            const tex = this.textures.get(biomeKey);
            const srcW = tex.source[0]?.width ?? 1920;
            const srcH = tex.source[0]?.height ?? 1080;
            const coverScale = Math.max(plateW / srcW, plateH / srcH);
            const cropW = plateW / coverScale;
            const cropH = plateH / coverScale;
            const cropX = (srcW - cropW) / 2;
            const cropY = (srcH - cropH) / 2;
            const bgImg = this.add.image(cardX2, plateY, biomeKey)
              .setDepth(0.25).setAlpha(isAct ? 1 : 0.85)
              .setScale(coverScale)
              .setCrop(cropX, cropY, cropW, cropH);
            // Dim unselected cards noticeably darker by applying a grey tint over the biome image
            if (!isAct) bgImg.setTint(0x5a4028);
          }
          // Inset gold highlight line — inside the outer wooden frame
          this.add.rectangle(cardX2, plateY, plateW - 6, plateH - 6, 0, 0)
            .setStrokeStyle(1, isAct ? 0xd4a855 : 0x5a3c18, 0.75).setDepth(0.3);

          portraitSlots.forEach(({ x: px, data: pd, flip }, pi) => {
            if (!pd) return;
            // Cleaner integer-ratio sizes for the 128px source (80/128 = 0.625, 48/128 = 0.375)
            const pSize = pi === 1 ? 80 : 48;
            const py    = cardY2 - 10 + (pi === 1 ? 0 : 4);
            const pKeyC = [pd.heroKey, pd.heroClass].filter((k): k is string => !!k)
              .find((k) => this.textures.exists(`hero_${k}`));
            if (pKeyC) {
              // Side-portraits are shown free-standing (no circular frame behind them).
              const SHADOW_LIFT2: Record<string, number> = { necromancer: -0.05, shaman: -0.05, druid: -0.05 };
              const liftFrac2 = SHADOW_LIFT2[pKeyC] ?? 0;
              // Cyrith's sorcerer sheet has the character art baked into the left third
              // of each 128px frame, so a centered-on-px sprite reads as shifted-left.
              // Nudge in source pixels, scaled by displayed pSize, mirrored when flipped.
              const PORTRAIT_X_NUDGE2: Record<string, number> = { sorcerer: 24 };
              const xNudge2 = Math.round((PORTRAIT_X_NUDGE2[pKeyC] ?? 0) * (pSize / 128)) * (flip ? -1 : 1);
              this.add.ellipse(px + xNudge2, py + pSize / 2 + 2 + Math.round(pSize * liftFrac2), pSize * 0.55, 4, 0x000000, 0.5).setDepth(0.4);
              const spr = this.add.sprite(px + xNudge2, py, `hero_${pKeyC}`)
                .setDisplaySize(pSize, pSize).setOrigin(0.5, 0.5).setDepth(1 + pi * 0.1)
                .setFlipX(flip);
              // Apply linear filter to smooth pixelation on downscaled sprites
              const tex = this.textures.get(`hero_${pKeyC}`);
              if (tex && tex.source[0]) tex.setFilter(Phaser.Textures.FilterMode.LINEAR);
              const idleK = `hero_${pKeyC}_idle`;
              if (this.anims.exists(idleK)) spr.play(idleK);
            }
            if (pi !== 1 && pd) {
              this.add.text(px, py + pSize / 2 + 8, pd.name?.split(' ')[0] ?? '', {
                fontSize: '7px', fontFamily: 'Georgia, serif', color: isAct ? '#8b6930' : '#4a3010',
                stroke: '#000', strokeThickness: 2,
              }).setOrigin(0.5, 0.5).setDepth(2);
            }
          });

          // Biome / stage label removed — backdrop alone conveys the biome

          // Name + level bottom — sit directly on the backdrop with thick stroke + shadow
          this.add.text(cardX2, cbot - 20, info.heroName || 'Hero', {
            fontSize: '12px', fontFamily: "'Cinzel Decorative', 'Palatino Linotype', Georgia, serif",
            color: isAct ? '#ffe080' : '#c8973a', fontStyle: 'bold',
            stroke: '#000', strokeThickness: 4, shadow: { offsetX: 0, offsetY: 1, color: '#000', blur: 3, fill: true },
          }).setOrigin(0.5, 0.5).setDepth(3);
          this.add.text(cardX2, cbot - 8, `Lv ${info.heroLevel}`, {
            fontSize: '10px', fontFamily: 'Georgia, serif',
            color: isAct ? '#ffd080' : '#a07840', fontStyle: 'bold',
            stroke: '#000', strokeThickness: 3, shadow: { offsetX: 0, offsetY: 1, color: '#000', blur: 2, fill: true },
          }).setOrigin(0.5, 0.5).setDepth(3);

        } else {
          // Empty card — visible "Empty Slot" hint paired with a soft prompt
          // pointing the new player at the Forge Your Legend CTA below.
          this.add.text(cardX2, cardY2 - 8, 'Empty Slot', {
            fontSize: '14px', fontFamily: "'Cinzel Decorative', Georgia, serif",
            color: '#c8973a', fontStyle: 'bold',
            stroke: '#000', strokeThickness: 3, shadow: { offsetX: 0, offsetY: 1, color: '#000', blur: 2, fill: true },
          }).setOrigin(0.5);
          this.add.text(cardX2, cardY2 + 10, 'Forge a hero to begin', {
            fontSize: '10px', fontFamily: 'Georgia, serif', color: '#a07840', fontStyle: 'italic',
            stroke: '#000', strokeThickness: 2,
          }).setOrigin(0.5);
        }

        // Inactive card: clickable to switch slot
        if (!isAct) {
          cardBg.setInteractive({ useHandCursor: true });
          cardBg.on('pointerover',  () => { cardBg.setFillStyle(0x1e1408, 0.97); cardBg.setStrokeStyle(1, 0xc8973a, 0.4); });
          cardBg.on('pointerout',   () => { cardBg.setFillStyle(0x140e06, 0.97); cardBg.setStrokeStyle(1, 0x3a2010, 0.55); });
          cardBg.on('pointerdown', () => {
            this.time.removeAllEvents(); this.tweens.killAll();
            this.gsm.switchSaveSlot(slot);
            this.scene.restart();
          });
        }
      } // end normal card

      // Sigil count badge (◆ 1/2/3) at top-center — visible on both inferno and normal cards.
      drawSigilBadge(cardX2, ctop, slot);
    });

    // ── Side LIST — EVERY save slot (including the active one in the
    // centre card) so the picked slot stays visible on the right after
    // selection. The active tile gets a brighter stroke + filled accent
    // square so it reads as "this is the current selection", not a
    // duplicate. Clicking the already-active tile is a no-op (no scene
    // restart, no flicker). [+] New Save and [+] New Inferno Save tiles
    // ride at the bottom of the list.
    const sideSlots = usedSlots;
    sideSlots.forEach((slot, sIdx) => {
      const sy = SIDE_Y0 + sIdx * (SIDE_H + SIDE_GAP);
      const info = this.gsm.getSaveSlotInfo(slot);
      if (!info) return;
      const isInferno = !!info.infernoOnly;
      const isActiveTile = slot === displaySlot;
      const fillC   = isInferno ? 0x14060a : 0x140e06;
      const strokeI = isInferno ? 0x6b1000 : 0x3a2010;
      const strokeH = isInferno ? 0xff4020 : 0xc8973a;
      const accentC = isInferno ? '#cc2200' : '#c8973a';
      const dimC    = isInferno ? '#a04020' : '#a07840';
      // Active tile reads as "selected" — bright stroke at full alpha matches
      // the hover state so the player can find their current slot at a glance.
      const restingStroke    = isActiveTile ? strokeH : strokeI;
      const restingStrokeAlpha = isActiveTile ? 1 : 0.85;

      const sBg = this.add.rectangle(SIDE_X, sy, SIDE_W, SIDE_H, fillC, 0.95)
        .setStrokeStyle(1, restingStroke, restingStrokeAlpha).setInteractive({ useHandCursor: true });
      this.add.rectangle(SIDE_X, sy, SIDE_W - 8, SIDE_H - 8, 0, 0)
        .setStrokeStyle(1, restingStroke, isActiveTile ? 0.55 : 0.30);

      const mainHero = info.heroData?.[0] ?? info.infernoHeroData?.[0];
      const heroName = (mainHero?.name ?? `Slot ${slot}`).toUpperCase();
      const heroLv = mainHero?.level ?? 1;
      this.add.text(SIDE_X - SIDE_W / 2 + 12, sy, `${slot}`, {
        fontSize: '14px', fontFamily: "'Cinzel Decorative', Georgia, serif",
        color: accentC, fontStyle: 'bold',
      }).setOrigin(0, 0.5);
      this.add.text(SIDE_X + 8, sy - 7, heroName, {
        fontSize: '10px', fontFamily: "'Cinzel Decorative', Georgia, serif",
        color: accentC, fontStyle: 'bold',
      }).setOrigin(0.5, 0.5);
      this.add.text(SIDE_X + 8, sy + 8, `Lv ${heroLv}`, {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: dimC,
      }).setOrigin(0.5, 0.5);

      sBg.on('pointerover', () => sBg.setStrokeStyle(1, strokeH, 1));
      sBg.on('pointerout', () => sBg.setStrokeStyle(1, restingStroke, restingStrokeAlpha));
      sBg.on('pointerdown', () => {
        if (isActiveTile) return; // already in the centre — no need to restart
        this.time.removeAllEvents(); this.tweens.killAll();
        this.gsm.switchSaveSlot(slot);
        this.scene.restart();
      });
    });

    // Position constants reused by the +card blocks below — anchored to the
    // bottom of the side list rather than the deck's right edge.
    const plusCardX = SIDE_X;
    let   plusCardY = SIDE_Y0 + sideSlots.length * (SIDE_H + SIDE_GAP);
    const PLUS_LIST_W = SIDE_W;
    const PLUS_LIST_H = SIDE_H;

    // [+] New Save mini-tile — same SIDE_W × SIDE_H footprint as a save tile
    // so the column reads uniformly. Inferno variant stacks beneath when
    // account.infernoUnlocked.
    if (canAddSlot) {
      const plusBg2 = this.add.rectangle(plusCardX, plusCardY, PLUS_LIST_W, PLUS_LIST_H, 0x0d0a06, 0.85)
        .setStrokeStyle(1, 0x5a3600, 0.6).setInteractive({ useHandCursor: true });
      const plusSymbol = this.add.text(plusCardX - 30, plusCardY, '+', {
        fontSize: '20px', fontFamily: 'Georgia, serif', color: '#6a4a20', fontStyle: 'bold',
      }).setOrigin(0.5);
      const plusLabel = this.add.text(plusCardX + 8, plusCardY, 'New Save', {
        fontSize: '10px', fontFamily: "'Cinzel Decorative', Georgia, serif", color: '#6a4a20', fontStyle: 'italic',
      }).setOrigin(0.5);
      plusBg2.on('pointerover', () => {
        plusBg2.setStrokeStyle(1, 0xc8973a, 0.85);
        plusSymbol.setColor('#c8973a'); plusLabel.setColor('#c8973a');
      });
      plusBg2.on('pointerout', () => {
        plusBg2.setStrokeStyle(1, 0x5a3600, 0.6);
        plusSymbol.setColor('#6a4a20'); plusLabel.setColor('#6a4a20');
      });
      plusBg2.on('pointerdown', () => {
        const nextSlot = [1,2,3,4,5].find((n) => !usedSlots.includes(n)) ?? 5;
        this.time.removeAllEvents(); this.tweens.killAll();
        this.gsm.switchSaveSlot(nextSlot, true);
        this.scene.restart();
      });
      plusCardY += PLUS_LIST_H + SIDE_GAP;

      // [+] New Inferno Save — gated on the account-wide infernoUnlocked
      // flag (flipped by the eternal_pyre quest claim). Stacks directly
      // beneath the regular New Save tile so the side list reads as
      // "saves → new save → new inferno".
      if (this.gsm.account.infernoUnlocked === true) {
        const infernoY = plusCardY;
        const ipBg = this.add.rectangle(plusCardX, infernoY, PLUS_LIST_W, PLUS_LIST_H, 0x14060a, 0.95)
          .setStrokeStyle(1, 0x8a2010, 0.7).setInteractive({ useHandCursor: true });
        this.add.rectangle(plusCardX, infernoY, PLUS_LIST_W - 8, PLUS_LIST_H - 8, 0, 0)
          .setStrokeStyle(1, 0x5a1808, 0.45);
        const ipSymbol = this.add.text(plusCardX - 30, infernoY, '🔥', {
          fontSize: '15px', fontFamily: 'Georgia, serif', color: '#cc2200',
        }).setOrigin(0.5);
        const ipLabel = this.add.text(plusCardX + 8, infernoY, 'New Inferno', {
          fontSize: '10px', fontFamily: "'Cinzel Decorative', Georgia, serif", color: '#a04020', fontStyle: 'italic',
        }).setOrigin(0.5);
        ipBg.on('pointerover', () => {
          ipBg.setStrokeStyle(1, 0xff4020, 0.85); ipLabel.setColor('#ff6030');
        });
        ipBg.on('pointerout', () => {
          ipBg.setStrokeStyle(1, 0x8a2010, 0.7); ipLabel.setColor('#a04020');
        });
        ipBg.on('pointerdown', () => {
          const nextSlot = [1,2,3,4,5].find((n) => !usedSlots.includes(n)) ?? 5;
          this.time.removeAllEvents(); this.tweens.killAll();
          this.gsm.switchSaveSlot(nextSlot, true, true);
          this.scene.restart();
        });
        let ipTip: Phaser.GameObjects.Text | null = null;
        ipBg.on('pointerover', () => {
          ipTip?.destroy();
          ipTip = this.add.text(plusCardX, infernoY - PLUS_LIST_H / 2 - 4,
            'Inferno Save\nHeroes start at level 1.\nEvery zone uses inferno scaling.\nThe endgame loop.',
            {
              fontSize: '10px', fontFamily: 'Georgia, serif', color: '#ff6030',
              backgroundColor: '#0d0a08', padding: { x: 8, y: 5 },
              align: 'center', lineSpacing: 2,
            },
          ).setOrigin(0.5, 1).setDepth(60);
        });
        ipBg.on('pointerout', () => { ipTip?.destroy(); ipTip = null; });
        void ipSymbol;
        // Advance the cursor below the inferno tile so callers tracking
        // "side list bottom y" (e.g. Delete Save) get the correct anchor.
        plusCardY += PLUS_LIST_H + SIDE_GAP;
      }
    }

    // ── "Delete Save" button — sits at the BOTTOM of the side list
    // (below New Save / New Inferno tiles), so it lives in the same
    // column as the save tabs. Per user direction. plusCardY tracks the
    // current bottom of the side list after the +Save and +Inferno tiles
    // have been laid out, so we just continue from there. Only shown
    // when the active slot has a hero.
    {
      const curSlot = this.gsm.player.saveSlot;
      // Only show Delete when the active slot has actual save data — an
      // empty-but-selected slot has nothing to delete, so the button just
      // confuses new users. Mirrors the `hasH` check used by the centre card.
      const curInfo = curSlot >= 1 ? this.gsm.getSaveSlotInfo(curSlot) : null;
      const curSlotHasHero = !!(curInfo && (curInfo.setupComplete || curInfo.heroes > 0));
      if (curSlotHasHero) {
        const delX = SIDE_X;
        // plusCardY has been advanced past every rendered side-list tile,
        // so it now points at the y where the next tile centre would go.
        // We place the Delete button at exactly that y for consistent
        // spacing with the tiles above. Delete is shorter (22 px vs the
        // 40-px tiles) so the visual rhythm reads as "tile, tile, … then
        // a smaller follow-up control".
        const delY = plusCardY - (SIDE_H - 22) / 2;
        const delW = SIDE_W;
        // Muted grey-with-border treatment — destructive action shouldn't
        // shout red for new users. Keeps the icon + label legible without
        // competing with the primary CTA.
        const DEL_FILL_IDLE   = 0x14100c;
        const DEL_STROKE_IDLE = 0x6a5a48;
        const DEL_FILL_HOVER  = 0x1f1810;
        const DEL_STROKE_HOVER = 0x9a8060;
        const DEL_TEXT_IDLE   = '#a08868';
        const DEL_TEXT_HOVER  = '#d4b890';
        const DEL_GLYPH_IDLE  = 0xa08868;
        const delBgBtn = this.add.rectangle(delX, delY, delW, 22, DEL_FILL_IDLE, 0.95)
          .setStrokeStyle(1, DEL_STROKE_IDLE, 0.85).setInteractive({ useHandCursor: true });
        const trashG = this.add.graphics();
        trashG.lineStyle(1, DEL_GLYPH_IDLE, 0.95);
        const tgx = delX - delW / 2 + 14;
        trashG.beginPath();
        trashG.moveTo(tgx - 2, delY - 6); trashG.lineTo(tgx + 2, delY - 6);
        trashG.moveTo(tgx - 5, delY - 4); trashG.lineTo(tgx + 5, delY - 4);
        trashG.moveTo(tgx - 4, delY - 3); trashG.lineTo(tgx - 3, delY + 5);
        trashG.moveTo(tgx + 4, delY - 3); trashG.lineTo(tgx + 3, delY + 5);
        trashG.moveTo(tgx - 3, delY + 5); trashG.lineTo(tgx + 3, delY + 5);
        trashG.strokePath();
        const delTxtBtn = this.add.text(delX + 6, delY, 'Delete Save', {
          fontSize: '9px', fontFamily: 'Georgia, serif',
          color: DEL_TEXT_IDLE, fontStyle: 'bold',
        }).setResolution(2).setOrigin(0.5).setInteractive({ useHandCursor: true });
        const dOver = () => { delBgBtn.setFillStyle(DEL_FILL_HOVER, 0.97).setStrokeStyle(1, DEL_STROKE_HOVER, 1); delTxtBtn.setColor(DEL_TEXT_HOVER); };
        const dOut  = () => { delBgBtn.setFillStyle(DEL_FILL_IDLE, 0.95).setStrokeStyle(1, DEL_STROKE_IDLE, 0.85); delTxtBtn.setColor(DEL_TEXT_IDLE); };
        delBgBtn.on('pointerover', dOver).on('pointerout', dOut).on('pointerdown', () => this.confirmDeleteSlot(curSlot, delX, delY));
        delTxtBtn.on('pointerover', dOver).on('pointerout', dOut).on('pointerdown', () => this.confirmDeleteSlot(curSlot, delX, delY));
      }
    }

    // ── Slot / hero state used by both inferno row and action buttons ──
    const slotSelected  = this.gsm.player.saveSlot >= 1;
    const hasActiveHero = slotSelected && (this.gsm.player.setupComplete || this.gsm.player.heroes.length > 0) && this.gsm.player.heroes.length > 0;

    // Inferno-eligibility is computed up front so the action-button block
    // below can render an "Inferno Mode" button right under "Enter the
    // Battlefield" instead of as a separate strip above. Per user direction
    // the standalone inferno row was retired so the centre column reads as
    // a single primary-action stack.
    const infernoUnlocked = hasActiveHero && this.gsm.player.currentLevel >= 40;

    // ── Action buttons ──
    // Quests / Codex / Multiplayer live in the bottom-left icon grid now —
    // the vertical stack only carries primary actions + the active-run context line.

    const BTN_W = 240;
    const BTN_GAP = 36;
    // Centre column moved up to make room for an Inferno button stacked
    // beneath the primary action when unlocked. The active save card sits
    // at y=197 with bottom edge at y=263, so the action stack starts well
    // below it and ends above the bottom-right tile row.
    const PRIMARY_Y = 444;

    if (!slotSelected) {
      this.add.text(cx, PRIMARY_Y, '— Choose a save rune to begin —', {
        fontSize: '11px', fontFamily: 'Georgia, serif', color: '#3d2e18',
      }).setOrigin(0.5);
    } else if (!hasActiveHero) {
      this.createMenuButton(cx, PRIMARY_Y, '⚔  Forge Your Legend', true, () => this.scene.start('HeroSelectScene'), BTN_W);
    } else if (this.gsm.player.infernoOnly) {
      // ── Inferno-Only heroes: fallen or active in Inferno ──
      const h = this.gsm.player.heroes[0];
      const infernoFallen = h?.level === 1;
      this.add.text(cx, PRIMARY_Y - 38, `${h?.name || 'Hero'}  ·  Lv ${h?.level || 1}`, {
        fontSize: '13px', fontFamily: 'Georgia, serif', color: '#e8784a', fontStyle: 'bold',
        stroke: '#1a0000', strokeThickness: 3,
      }).setOrigin(0.5);
      this.add.text(cx, PRIMARY_Y - 22, '☠  INFERNO  ☠', {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#7a2a10', letterSpacing: 2,
      }).setOrigin(0.5);
      if (infernoFallen) {
        this.add.text(cx, PRIMARY_Y - 6, '— Consumed by Hellfire —', {
          fontSize: '11px', fontFamily: 'Georgia, serif', color: '#5a1a08', fontStyle: 'italic',
        }).setOrigin(0.5);
        this.createMenuButton(cx, PRIMARY_Y + BTN_GAP, '⚔  Forge Your Legend', true, () => {
          this.gsm.resetRun();
          this.gsm.save();
          this.scene.start('HeroSelectScene');
        }, BTN_W);
      } else {
        this.createMenuButton(cx, PRIMARY_Y, '☠  Continue Inferno', true, () => {
          this.stopMusic();
          this.scene.start('BattleScene', {
            inferno: true,
            infernoZone: this.gsm.player.infernoZone ?? 1,
            infernoEntryLevel: this.gsm.player.infernoEntryLevel ?? 39,
          });
        }, BTN_W);
      }
    } else {
      const h = this.gsm.player.heroes[0];
      this.add.text(cx, PRIMARY_Y - 30, `${h?.name || 'Hero'}  ·  Lv${h?.level || 1}  ·  Stage ${this.gsm.player.currentLevel}`, {
        fontSize: '13px', fontFamily: 'Georgia, serif', color: '#d4a855', fontStyle: 'bold',
        shadow: { offsetX: 1, offsetY: 2, color: '#000', blur: 4, fill: true },
      }).setOrigin(0.5);
      this.createMenuButton(cx, PRIMARY_Y, '⚔  Enter the Battlefield', true, () => {
        this.stopMusic();
        const setupDone = !!this.gsm.player.preRunSetupDone;
        const isFresh = this.gsm.player.currentLevel === 1
          && this.gsm.battle.waveNumber === 0
          && (this.gsm.player.stayCount ?? 0) === 0;
        if (isFresh && !setupDone) {
          this.scene.start('SigilLoadoutScene');
        } else {
          this.scene.start('BattleScene', { inferno: false });
        }
      }, BTN_W);
      // Inferno Mode — stacks directly beneath Enter the Battlefield when
      // unlocked, replacing the old standalone INFERNO_Y strip per user
      // direction so the centre column reads as a single primary stack.
      // Locked variant shows the gate condition so first-timers know what
      // to push for.
      if (infernoUnlocked) {
        const infY = PRIMARY_Y + BTN_GAP;
        const infBg = this.add.rectangle(cx, infY, BTN_W, 30, 0x1e0404, 0.96)
          .setStrokeStyle(1, 0x8b1a00).setInteractive({ useHandCursor: true });
        const fg = this.add.graphics();
        const fx = cx - BTN_W / 2 + 22; const fy = infY;
        fg.fillStyle(0x8b1a00, 0.9); fg.fillTriangle(fx, fy - 8, fx - 6, fy + 7, fx + 6, fy + 7);
        fg.fillStyle(0xcc2200, 0.8); fg.fillTriangle(fx, fy - 5, fx - 4, fy + 6, fx + 4, fy + 6);
        fg.fillStyle(0xff4a10, 0.6); fg.fillTriangle(fx, fy - 1, fx - 2, fy + 5, fx + 2, fy + 5);
        const infLabel = this.add.text(cx + 4, infY, 'INFERNO MODE', {
          fontSize: '12px', fontFamily: 'Georgia, serif', color: '#b83010', fontStyle: 'bold',
          letterSpacing: 2,
        }).setOrigin(0.5);
        const hiScore = this.gsm.player.infernoHighScore ?? 0;
        if (hiScore > 0) {
          this.add.text(cx + BTN_W / 2 - 6, infY, `best ${hiScore.toLocaleString()}`, {
            fontSize: '7px', fontFamily: 'Georgia, serif', color: '#5a1a08',
          }).setOrigin(1, 0.5);
        }
        const doInferno = () => this.showInfernoConfirm();
        infBg.on('pointerover', () => { infBg.setFillStyle(0x2e0808, 0.98); infBg.setStrokeStyle(1, 0xcc2200); infLabel.setColor('#d84020'); });
        infBg.on('pointerout',  () => { infBg.setFillStyle(0x1e0404, 0.96); infBg.setStrokeStyle(1, 0x8b1a00); infLabel.setColor('#b83010'); });
        infBg.on('pointerdown', doInferno);
        infLabel.setInteractive({ useHandCursor: true }).on('pointerdown', doInferno);
      } else {
        // Locked: muted strip showing the gate condition (stage 40).
        const infY = PRIMARY_Y + BTN_GAP;
        this.add.rectangle(cx, infY, BTN_W, 30, 0x100602, 0.7)
          .setStrokeStyle(1, 0x3a1808, 0.6);
        this.add.text(cx - BTN_W / 2 + 12, infY, '— INFERNO MODE', {
          fontSize: '10px', fontFamily: 'Georgia, serif', color: '#3a1808', fontStyle: 'italic',
        }).setOrigin(0, 0.5);
        this.add.text(cx + BTN_W / 2 - 8, infY, 'Stage 40 required', {
          fontSize: '8px', fontFamily: 'Georgia, serif', color: '#3a1808',
        }).setOrigin(1, 0.5);
      }
      // "Begin New Journey" sits below the inferno line so the stack reads
      // [Hero info | Enter | Inferno | Begin Journey]. Always shown so the
      // reset path stays one click away even when inferno is locked.
      this.createMenuButton(cx, PRIMARY_Y + BTN_GAP * 2, '✦  Begin New Journey', false, () => this.showNewJourneyConfirm(), BTN_W);
    }

    // ── Bottom-RIGHT tile row: [Quests · Hero Armory · Ancestral Tree · Champions Board] ──
    // Single horizontal row at the bottom, each tile labelled. Tile size 58 px
    // (down from 74) per user direction — these were "too big" relative to
    // the new top-left toolbar; the lobby now reads in size hierarchy as
    // [save card] > [bottom action tiles] > [top-left nav]. The vendor cat
    // keeps her perch on top of the Ancestral Tree tile.
    const RIGHT_TILE_SIZE = 58;
    const RIGHT_ROW_Y = H - 56;
    const RIGHT_TILE_GAP = RIGHT_TILE_SIZE + 8; // 8-px breathing room between tiles
    const rightRowRightCx = W - RIGHT_TILE_SIZE / 2 - 12;
    const RIGHT_CHAMPIONS_X = rightRowRightCx;
    const RIGHT_TREE_X      = rightRowRightCx - 1 * RIGHT_TILE_GAP;
    const RIGHT_ARMORY_X    = rightRowRightCx - 2 * RIGHT_TILE_GAP;
    const RIGHT_QUESTS_X    = rightRowRightCx - 3 * RIGHT_TILE_GAP;

    const rightLabelY = RIGHT_ROW_Y + RIGHT_TILE_SIZE / 2 + 10;
    const addRightLabel = (lx: number, txt: string): Phaser.GameObjects.Text =>
      this.add.text(lx, rightLabelY, txt, {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#a07840',
      }).setOrigin(0.5).setDepth(11);

    const rightUnclaimed = this.gsm.getUnclaimedAchievementCount();
    this.createRightIconButton(RIGHT_QUESTS_X, RIGHT_ROW_Y, '✦', 0x0e0b08, 0x6a4a20,
      () => { this.stopMusic(); this.scene.start('AchievementScene', { gsm: this.gsm }); },
      'Quests', RIGHT_TILE_SIZE);
    addRightLabel(RIGHT_QUESTS_X, 'Quests');
    if (rightUnclaimed > 0) {
      const bx = RIGHT_QUESTS_X + RIGHT_TILE_SIZE / 2 - 6;
      const by = RIGHT_ROW_Y - RIGHT_TILE_SIZE / 2 + 6;
      this.add.circle(bx, by, 7, 0x8b1a00, 0.95).setStrokeStyle(1, 0xcc2200).setDepth(13);
      this.add.text(bx, by, `${rightUnclaimed}`, {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#ffe0a0', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(14);
    }

    this.createRightIconButton(RIGHT_ARMORY_X, RIGHT_ROW_Y, 'ᛟ', 0x0e0b08, 0x6a4a20,
      () => { this.scene.start('ArmoryScene', { gsm: this.gsm }); },
      'Hero Armory', RIGHT_TILE_SIZE);
    addRightLabel(RIGHT_ARMORY_X, 'Hero Armory');

    this.createRightIconButton(RIGHT_TREE_X, RIGHT_ROW_Y, 'ᚱ', 0x0e0b08, 0x6a4a20,
      () => { this.scene.start('PassiveTreeScene', { gsm: this.gsm }); },
      'Ancestral Rune Tree', RIGHT_TILE_SIZE);
    addRightLabel(RIGHT_TREE_X, 'Ancestral Tree');

    this.createRightIconButton(RIGHT_CHAMPIONS_X, RIGHT_ROW_Y, '★', 0x140304, 0x8b1a00,
      () => this.showLeaderboardPopup(),
      'Champions Board', RIGHT_TILE_SIZE);
    addRightLabel(RIGHT_CHAMPIONS_X, 'Champions');

    // ── Vendor cat (Animal_6 Idle) — perched on top of the Ancestral Tree tile.
    //     Click → Sigil vendor panel (shop + bag + equipped slots).
    if (this.textures.exists('vendor_cat_idle')) {
      // Cat sits with feet on the Tree tile's top edge (size scaled down too).
      const CAT_X = RIGHT_TREE_X;
      const CAT_FEET_Y = RIGHT_ROW_Y - RIGHT_TILE_SIZE / 2;
      const CAT_SIZE = 92;
      const cat = this.add.sprite(CAT_X, CAT_FEET_Y, 'vendor_cat_idle')
        .setDisplaySize(CAT_SIZE, CAT_SIZE).setOrigin(0.5, 1.0).setDepth(5)
        .setFlipX(true)
        .setInteractive({ useHandCursor: true });
      if (this.anims.exists('vendor_cat_idle')) cat.play('vendor_cat_idle');
      let catTip: Phaser.GameObjects.Container | Phaser.GameObjects.Text | null = null;
      cat.on('pointerover', () => {
        cat.setTint(0xffe8a0);
        // Tooltip sits directly above the cat's head — bottom-anchored so it
        // hovers on top of the sprite instead of floating off to the side.
        const tipX = CAT_X;
        const tipY = CAT_FEET_Y - CAT_SIZE + 6;
        catTip = createHudTooltip(this, tipX, tipY, 'Sigil Vendor',
          0.5, 1, { fontSize: '9px', depth: 40 });
      });
      cat.on('pointerout', () => { cat.clearTint(); catTip?.destroy(); catTip = null; });
      cat.on('pointerdown', () => {
        catTip?.destroy(); catTip = null;
        this.gsm.refreshSigilVendor();
        this.showSigilVendorPopup();
      });
    }

    this.add.text(W - 12, H - 8, 'v6.0.0-alpha', {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: '#2d2018',
    }).setOrigin(1, 1);
  }

  /** Right-side decorative square icon button — icon only, textured frame.
   *  Optional `tooltip` shows on hover (positioned above the tile, single
   *  line) so the player sees what each button opens. */
  private createRightIconButton(
    x: number, y: number,
    icon: string,
    fillColor: number, borderColor: number,
    onClick: () => void,
    tooltip?: string,
    size = 74,
  ): void {
    const SZ = size;
    const half = SZ / 2;
    // Icon font scales with the tile so the glyph fills the smaller variant
    // proportionally. Default 34px at SZ=74 → keep the same 0.46 ratio.
    const iconFontPx = Math.round(SZ * 0.46);
    const toHex = (n: number) => `#${Math.min(0xffffff, n).toString(16).padStart(6, '0')}`;

    // Outer border (slightly brighter than fill, solid)
    const outerBorder = this.add.rectangle(x, y, SZ, SZ, fillColor, 0.0)
      .setStrokeStyle(2, borderColor, 1.0);

    // Main fill
    const bg = this.add.rectangle(x, y, SZ - 2, SZ - 2, fillColor, 0.97)
      .setInteractive({ useHandCursor: true });

    // Inner inset frame (4px inside outer edge)
    this.add.rectangle(x, y, SZ - 12, SZ - 12, 0, 0)
      .setStrokeStyle(1, borderColor, 0.30);

    // Corner accent marks — four L-shapes drawn via Graphics
    const accentColor = Math.min(0xffffff, borderColor + 0x181008);
    const acc = this.add.graphics();
    acc.lineStyle(1, accentColor, 0.75);
    const cLen = 9; // length of each corner arm
    const margin = 5; // inset from edge
    const x0 = x - half + margin, x1 = x + half - margin;
    const y0 = y - half + margin, y1 = y + half - margin;
    // top-left
    acc.strokePoints([{ x: x0, y: y0 + cLen }, { x: x0, y: y0 }, { x: x0 + cLen, y: y0 }], false);
    // top-right
    acc.strokePoints([{ x: x1 - cLen, y: y0 }, { x: x1, y: y0 }, { x: x1, y: y0 + cLen }], false);
    // bottom-left
    acc.strokePoints([{ x: x0, y: y1 - cLen }, { x: x0, y: y1 }, { x: x0 + cLen, y: y1 }], false);
    // bottom-right
    acc.strokePoints([{ x: x1 - cLen, y: y1 }, { x: x1, y: y1 }, { x: x1, y: y1 - cLen }], false);

    // Icon — large, centered. Font scales with tile size.
    const iconTxt = this.add.text(x, y, icon, {
      fontSize: `${iconFontPx}px`, fontFamily: 'Georgia, serif', color: toHex(borderColor),
    }).setOrigin(0.5);
    iconTxt.setInteractive({ useHandCursor: true });

    const hoverFill   = Math.min(0xffffff, fillColor   + 0x0c0a06);
    const hoverBorder = Math.min(0xffffff, borderColor + 0x282010);
    let tipObj: Phaser.GameObjects.Container | Phaser.GameObjects.Text | null = null;
    bg.on('pointerover', () => {
      bg.setFillStyle(hoverFill, 0.99);
      outerBorder.setStrokeStyle(2, hoverBorder, 1.0);
      iconTxt.setColor(toHex(hoverBorder));
      if (tooltip) {
        tipObj?.destroy();
        tipObj = createHudTooltip(this, x, y - half - 6, tooltip,
          0.5, 1, { fontSize: '10px', depth: 60 });
      }
    });
    bg.on('pointerout', () => {
      bg.setFillStyle(fillColor, 0.97);
      outerBorder.setStrokeStyle(2, borderColor, 1.0);
      iconTxt.setColor(toHex(borderColor));
      tipObj?.destroy();
      tipObj = null;
    });
    // Button-click SFX disabled — current `button_click.wav` reads off-tone.
    // Re-enable by reinstating the SoundManager().play('ui_click') wrapper
    // once a better clip is dropped into public/assets/sfx/.
    bg.on('pointerdown', onClick);
    iconTxt.on('pointerdown', onClick);
  }

  /** Small square icon button used in the bottom-left 2×2 nav grid.
   *  Bronze tile + drawn Graphics glyph + label beneath. Optional red count badge.
   *  Never uses emoji — `paint` is a GridIconPainter that renders the glyph via primitives. */
  /** Returns true when the current save slot has loaded save data with at
   *  least one hero — the precondition for entering the multiplayer menu.
   *  The null-safe chain keeps the button defaulting to disabled when the
   *  save data hasn't loaded yet (fresh-login race). */
  public isMultiplayerEnabled(): boolean {
    // Steam build (FEATURE_MULTIPLAYER=false) hides MP entirely — no grid
    // button, no lobby route, no invite toast wiring. Returning false here
    // disables the button visually AND prevents the click handler from
    // navigating to a lobby scene that would try to open a socket.
    if (!FEATURE_MULTIPLAYER) return false;
    const slot = this.gsm?.player?.saveSlot;
    if (slot === undefined || slot < 1) return false;
    const info = this.gsm.getSaveSlotInfo(slot);
    return (info?.heroData?.length ?? 0) > 0;
  }

  private createGridIconButton(
    x: number, y: number,
    paint: GridIconPainter, label: string,
    onClick: () => void,
    badge?: string,
    disabled = false,
    disabledTooltip?: string,
    size = 54,
  ): void {
    const SZ = size;
    const half = SZ / 2;
    const outer = this.add.rectangle(x, y, SZ, SZ, 0x0e0b08, 0.0)
      .setStrokeStyle(2, 0x6a4a20, 1.0).setDepth(10);
    const bg = this.add.rectangle(x, y, SZ - 2, SZ - 2, 0x0e0b08, 0.95)
      .setDepth(10).setInteractive({ useHandCursor: true });
    this.add.rectangle(x, y, SZ - 10, SZ - 10, 0, 0)
      .setStrokeStyle(1, 0x6a4a20, 0.30).setDepth(10);
    // Corner L-shaped accents
    const acc = this.add.graphics().setDepth(11);
    acc.lineStyle(1, 0x826030, 0.75);
    const cLen = 7;
    const margin = 4;
    const x0 = x - half + margin, x1 = x + half - margin;
    const y0 = y - half + margin, y1 = y + half - margin;
    acc.strokePoints([{ x: x0, y: y0 + cLen }, { x: x0, y: y0 }, { x: x0 + cLen, y: y0 }], false);
    acc.strokePoints([{ x: x1 - cLen, y: y0 }, { x: x1, y: y0 }, { x: x1, y: y0 + cLen }], false);
    acc.strokePoints([{ x: x0, y: y1 - cLen }, { x: x0, y: y1 }, { x: x0 + cLen, y: y1 }], false);
    acc.strokePoints([{ x: x1 - cLen, y: y1 }, { x: x1, y: y1 }, { x: x1, y: y1 - cLen }], false);

    // Drawn glyph — two tones (light bronze / dark bronze) to match the menu palette
    // Match the muted bronze of the grid labels at rest; brighten on hover so the
    // tile still reads as interactive without the icons screaming yellow.
    const FG_IDLE = 0xa07840;
    const FG_HOVER = 0xc8973a;
    const BG_IDLE = 0x4a3010;
    const iconGfx = this.add.graphics().setDepth(12);
    const repaint = (fg: number, bgTint: number): void => { iconGfx.clear(); paint(iconGfx, x, y - 2, fg, bgTint); };
    repaint(FG_IDLE, BG_IDLE);

    const labelTxt = this.add.text(x, y + SZ / 2 + 8, label, {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: '#a07840',
    }).setOrigin(0.5).setDepth(11);

    if (badge) {
      const bx = x + half - 4, by = y - half + 4;
      this.add.circle(bx, by, 7, 0x8b1a00, 0.95).setStrokeStyle(1, 0xcc2200).setDepth(12);
      this.add.text(bx, by, badge, {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#ffe0a0', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(13);
    }

    if (disabled) {
      // Greyed-out treatment — opacity 0.4, no hand cursor, no click handler.
      // Tooltip surfaces on hover so the player understands why the button is
      // inactive. Hooking setInteractive without useHandCursor keeps pointer
      // events flowing so the tooltip can still track hover, but we don't wire
      // the onClick callback — click is a no-op.
      outer.setAlpha(0.4);
      bg.setAlpha(0.4);
      acc.setAlpha(0.4);
      iconGfx.setAlpha(0.4);
      labelTxt.setAlpha(0.4).setColor('#5a4830');
      // Remove the default hand cursor interactivity registered above, then
      // re-register WITHOUT the cursor hint.
      bg.disableInteractive();
      bg.setInteractive();

      // Tooltip above the button — hidden by default, fades in on hover.
      let tooltip: Phaser.GameObjects.Text | undefined;
      bg.on('pointerover', () => {
        if (!disabledTooltip) return;
        tooltip = this.add.text(x, y - SZ / 2 - 14, disabledTooltip, {
          fontSize: '10px', fontFamily: 'Georgia, serif', color: '#d4a855',
          backgroundColor: '#1a1208', padding: { x: 6, y: 3 },
        }).setOrigin(0.5, 1).setDepth(30);
      });
      bg.on('pointerout', () => {
        tooltip?.destroy();
        tooltip = undefined;
      });
      return;
    }

    bg.on('pointerover', () => {
      bg.setFillStyle(0x1a120a, 0.98);
      outer.setStrokeStyle(2, 0xc8973a, 1.0);
      repaint(FG_HOVER, BG_IDLE);
      labelTxt.setColor('#c8973a');
    });
    bg.on('pointerout', () => {
      bg.setFillStyle(0x0e0b08, 0.95);
      outer.setStrokeStyle(2, 0x6a4a20, 1.0);
      repaint(FG_IDLE, BG_IDLE);
      labelTxt.setColor('#a07840');
    });
    bg.on('pointerdown', onClick);
  }

  /** Scrollable leaderboard popup overlay — tabbed: Standard / Inferno */
  private showLeaderboardPopup(startTab: 'standard' | 'inferno' = 'standard'): void {
    const W = 960;
    const H = 576;
    const cx = W / 2;
    const cy = H / 2;
    // Sized to comfortably fit 8-digit scores (11M+) without crunching the
    // adjacent columns — was 600×440 and a long score number visually
    // crashed into the Stage column. 720×500 gives every column enough
    // air, and the extra height fits one more row of entries.
    const ROWS = 8;
    const panelW = 720;
    const panelH = 500;
    const ROW_H = 34;
    const elements: Phaser.GameObjects.GameObject[] = [];
    const rowObjects: Phaser.GameObjects.GameObject[] = [];
    const tabObjects: Phaser.GameObjects.GameObject[] = [];
    const filterDropdownObjects: Phaser.GameObjects.GameObject[] = [];

    let activeTab: 'standard' | 'inferno' = startTab;
    let scrollOffset = 0;
    let filterHero: string = 'all'; // hero key filter, 'all' = show every hero
    let filterOpen = false;
    // Sort state — Score / Stage columns toggle direction on click; the
    // active column shows ▼ (desc) or ▲ (asc) next to its header text.
    // Default mirrors the server-side ordering so a fresh open looks the
    // same as before sorting was a thing.
    let sortKey: 'score' | 'stage' = 'score';
    let sortDir: 'desc' | 'asc' = 'desc';

    let stdBoard: LeaderboardEntry[] = [];
    let infBoard: (InfernoLeaderboardEntry & { level?: number })[] = [];
    let loading = true;

    // ── Overlay + double-bordered panel ──
    const overlay = this.add.rectangle(cx, cy, W, H, 0x000000, 0.82).setDepth(80).setInteractive();
    elements.push(overlay);
    elements.push(
      this.add.rectangle(cx, cy, panelW, panelH, 0x0e0b08, 0.98).setStrokeStyle(2, 0x8b6420).setDepth(81),
      this.add.rectangle(cx, cy, panelW - 8, panelH - 8, 0, 0).setStrokeStyle(1, 0x5a3600, 0.55).setDepth(81),
    );

    // ── STYLISH TITLE — Cinzel Decorative, multi-layer drop+main+highlight ──
    // Font size 26 → 20 to match the smaller panel; shorter flourish lines
    // anchored to the new panel edges so the ornament doesn't bleed past.
    const titleY = cy - panelH / 2 + 26;
    const titleText = 'CHAMPIONS BOARD';
    elements.push(
      this.add.text(cx + 2, titleY + 2, titleText, {
        fontSize: '20px', fontFamily: "'Cinzel Decorative', Georgia, serif",
        color: '#000000', fontStyle: 'bold', stroke: '#000000', strokeThickness: 4,
      }).setOrigin(0.5).setDepth(82).setAlpha(0.6),
      this.add.text(cx, titleY, titleText, {
        fontSize: '20px', fontFamily: "'Cinzel Decorative', Georgia, serif",
        color: '#d4952a', fontStyle: 'bold', stroke: '#5a2e00', strokeThickness: 2,
        shadow: { offsetX: 1, offsetY: 2, color: '#1a0a00', blur: 3, fill: true, stroke: false },
      }).setOrigin(0.5).setDepth(82),
      this.add.text(cx, titleY - 1, titleText, {
        fontSize: '20px', fontFamily: "'Cinzel Decorative', Georgia, serif",
        color: '#ffe8a0', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(82).setAlpha(0.18),
    );
    // Ornamental flourish — bronze line + diamond stops on each side of the title
    const flourY = titleY + 18;
    elements.push(
      this.add.rectangle(cx - 150, flourY, 140, 1, 0x7a4a00, 0.7).setDepth(82),
      this.add.rectangle(cx + 150, flourY, 140, 1, 0x7a4a00, 0.7).setDepth(82),
      this.add.text(cx - 80,  flourY, '✦', { fontSize: '10px', color: '#7a4a00' }).setOrigin(0.5).setDepth(82).setAlpha(0.85),
      this.add.text(cx + 80,  flourY, '✦', { fontSize: '10px', color: '#7a4a00' }).setOrigin(0.5).setDepth(82).setAlpha(0.85),
    );

    // ── Close button (top-right) — bronze palette, matches the close-X
    // pattern used by every other scene / popup (Inventory, Hero,
    // SigilLoadout, Sigil Vendor, etc.). Was red previously, which made
    // the leaderboard the only outlier in the close-X language.
    const closeBtn = this.add.text(cx + panelW / 2 - 18, cy - panelH / 2 + 18, '✕', {
      fontSize: '14px', fontFamily: 'Georgia, serif', color: '#5a3600',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setDepth(83);
    closeBtn.on('pointerover', () => closeBtn.setColor('#c8973a'));
    closeBtn.on('pointerout', () => closeBtn.setColor('#5a3600'));
    // Books column tooltip — single shared tooltip that follows the
    // hovered tome icon. Declared up here so cleanup() can drop it on
    // popup close (otherwise a tooltip showing at the moment the
    // player hits ✕ orphans on the canvas).
    let bookTip: Phaser.GameObjects.Container | null = null;
    const cleanup = (): void => {
      this.input.off('wheel', wheelHandler);
      elements.forEach((e) => e.destroy());
      rowObjects.forEach((e) => e.destroy());
      tabObjects.forEach((e) => e.destroy());
      filterDropdownObjects.forEach((e) => e.destroy());
      bookTip?.destroy();
      bookTip = null;
    };
    closeBtn.on('pointerdown', cleanup);
    elements.push(closeBtn);
    if (this.input.keyboard) this.input.keyboard.once('keydown-ESC', cleanup);

    // ── Tabs (Standard / Inferno) ──
    const tabY = cy - panelH / 2 + 64;
    const tabStdBg  = this.add.rectangle(cx - 70, tabY, 130, 24, 0x0e0b08, 1).setDepth(82).setInteractive({ useHandCursor: true }).setStrokeStyle(1, 0x3d2a0a);
    const tabInfBg  = this.add.rectangle(cx + 70, tabY, 130, 24, 0x0e0b08, 1).setDepth(82).setInteractive({ useHandCursor: true }).setStrokeStyle(1, 0x3d2a0a);
    const tabStdTxt = this.add.text(cx - 70, tabY, '⚔  Standard', {
      fontSize: '11px', fontFamily: "'Cinzel Decorative', Georgia, serif", color: '#7a6540', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(83).setInteractive({ useHandCursor: true });
    const tabInfTxt = this.add.text(cx + 70, tabY, '☠  Inferno', {
      fontSize: '11px', fontFamily: "'Cinzel Decorative', Georgia, serif", color: '#7a6540', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(83).setInteractive({ useHandCursor: true });
    tabObjects.push(tabStdBg, tabInfBg, tabStdTxt, tabInfTxt);

    const updateTabs = (): void => {
      if (activeTab === 'standard') {
        tabStdBg.setFillStyle(0x2a1a08, 1).setStrokeStyle(2, 0xc8973a);
        tabStdTxt.setColor('#c8973a');
        tabInfBg.setFillStyle(0x0e0b08, 1).setStrokeStyle(1, 0x3d2a0a);
        tabInfTxt.setColor('#7a6540');
      } else {
        tabInfBg.setFillStyle(0x2a0808, 1).setStrokeStyle(2, 0xcc2200);
        tabInfTxt.setColor('#ff6030');
        tabStdBg.setFillStyle(0x0e0b08, 1).setStrokeStyle(1, 0x3d2a0a);
        tabStdTxt.setColor('#7a6540');
      }
    };
    updateTabs();
    const switchTab = (tab: 'standard' | 'inferno'): void => {
      activeTab = tab;
      scrollOffset = 0;
      updateTabs();
      renderRows();
    };
    tabStdBg.on('pointerdown', () => switchTab('standard'));
    tabStdTxt.on('pointerdown', () => switchTab('standard'));
    tabInfBg.on('pointerdown', () => switchTab('inferno'));
    tabInfTxt.on('pointerdown', () => switchTab('inferno'));

    // ── Column headers — Hero header doubles as the filter dropdown ──
    // Layout: [#] [Hero ▾] [Name] [Score] [Stage] [Books]
    // Both headers and row data center on the SAME column-center X so the
    // table reads as a clean grid. Centers re-tuned for the 720 px panel
    // (676 px usable inner width) — Score gets extra horizontal room for
    // 8-digit numbers like "11,234,567" without crowding the Stage col.
    const tableLeft = cx - panelW / 2 + 22;
    const COL_RANK_CX  = 30;
    const COL_HERO_CX  = 92;
    const COL_NAME_CX  = 220;
    const COL_SCORE_CX = 366;
    const COL_ZONE_CX  = 480;
    const COL_BOOKS_CX = 590;
    const hY = cy - panelH / 2 + 108;
    const headerStyle = {
      fontSize: '11px', fontFamily: "'Cinzel Decorative', Georgia, serif",
      color: '#8a6420', fontStyle: 'bold',
    } as const;
    elements.push(
      this.add.text(tableLeft + COL_RANK_CX,   hY, '#',     headerStyle).setOrigin(0.5, 0).setDepth(82),
      this.add.text(tableLeft + COL_NAME_CX,   hY, 'Name',  headerStyle).setOrigin(0.5, 0).setDepth(82),
      this.add.text(tableLeft + COL_BOOKS_CX,  hY, 'Books', headerStyle).setOrigin(0.5, 0).setDepth(82),
    );

    // Sortable headers — Score and Stage. Clicking the active column flips
    // direction (▼ ↔ ▲); clicking the other column switches the active
    // sort key and resets to ▼. Initial state shows "Score ▼" since that
    // matches the server's default ordering.
    const scoreHeaderTxt = this.add.text(tableLeft + COL_SCORE_CX, hY, 'Score', headerStyle)
      .setOrigin(0.5, 0).setDepth(82).setInteractive({ useHandCursor: true });
    const stageHeaderTxt = this.add.text(tableLeft + COL_ZONE_CX, hY, 'Stage', headerStyle)
      .setOrigin(0.5, 0).setDepth(82).setInteractive({ useHandCursor: true });
    elements.push(scoreHeaderTxt, stageHeaderTxt);

    const updateSortHeaders = (): void => {
      const arrow = sortDir === 'desc' ? ' ▼' : ' ▲';
      scoreHeaderTxt.setText('Score' + (sortKey === 'score' ? arrow : ''));
      stageHeaderTxt.setText('Stage' + (sortKey === 'stage' ? arrow : ''));
      // Active column gets a brighter color so the eye lands on it first.
      scoreHeaderTxt.setColor(sortKey === 'score' ? '#c8973a' : '#8a6420');
      stageHeaderTxt.setColor(sortKey === 'stage' ? '#c8973a' : '#8a6420');
    };
    const setSort = (key: 'score' | 'stage'): void => {
      if (sortKey === key) {
        sortDir = sortDir === 'desc' ? 'asc' : 'desc';
      } else {
        sortKey = key;
        sortDir = 'desc';
      }
      scrollOffset = 0;
      updateSortHeaders();
      renderRows();
    };
    scoreHeaderTxt.on('pointerover', () => { if (sortKey !== 'score') scoreHeaderTxt.setColor('#c8973a'); });
    scoreHeaderTxt.on('pointerout',  () => updateSortHeaders());
    scoreHeaderTxt.on('pointerdown', () => setSort('score'));
    stageHeaderTxt.on('pointerover', () => { if (sortKey !== 'stage') stageHeaderTxt.setColor('#c8973a'); });
    stageHeaderTxt.on('pointerout',  () => updateSortHeaders());
    stageHeaderTxt.on('pointerdown', () => setSort('stage'));
    updateSortHeaders();

    // Hero header acts as the filter dropdown. Click → opens menu.
    const heroLabel = (key: string): string => {
      if (key === 'all') return 'All';
      const opt = HERO_OPTIONS.find((h) => (h.heroKey ?? h.heroClass) === key);
      return opt?.name ?? key;
    };
    const heroHeaderX = tableLeft + COL_HERO_CX;
    // Hover bg behind the header text so the click target is generous and the
    // user reads it as interactive ("Hero ▾").
    const heroHeaderBg = this.add.rectangle(heroHeaderX, hY + 4, 56, 18, 0x140e06, 0)
      .setOrigin(0.5).setDepth(81.7).setStrokeStyle(1, 0x3a2810, 0)
      .setInteractive({ useHandCursor: true });
    const heroHeaderTxt = this.add.text(heroHeaderX, hY, `Hero ${filterHero === 'all' ? '' : '· ' + heroLabel(filterHero)} ▾`, headerStyle).setOrigin(0.5, 0).setDepth(82);
    elements.push(heroHeaderBg, heroHeaderTxt);
    elements.push(this.add.rectangle(cx, hY + 18, panelW - 60, 1, 0x5a3600, 0.85).setDepth(82));

    const setFilter = (key: string): void => {
      filterHero = key;
      heroHeaderTxt.setText(`Hero ${key === 'all' ? '' : '· ' + heroLabel(key)} ▾`);
      scrollOffset = 0;
      filterOpen = false;
      filterDropdownObjects.forEach((o) => o.destroy());
      filterDropdownObjects.length = 0;
      renderRows();
    };
    const toggleFilter = (): void => {
      if (filterOpen) {
        filterDropdownObjects.forEach((o) => o.destroy());
        filterDropdownObjects.length = 0;
        filterOpen = false;
        return;
      }
      const board = activeTab === 'standard' ? stdBoard : infBoard;
      const presentKeys = new Set<string>();
      for (const e of board) { if (e.heroKey) presentKeys.add(e.heroKey); }
      const opts: string[] = ['all', ...Array.from(presentKeys).sort()];
      const optH = 22;
      const optW = 140;
      // heroHeaderX is now the column-center; anchor the dropdown a bit
      // to the right so the menu hangs under the dropdown caret rather
      // than off the left side of the header text.
      const dropX = heroHeaderX + 30;
      const dropTop = hY + 22;
      filterDropdownObjects.push(
        this.add.rectangle(dropX, dropTop + (optH * opts.length) / 2, optW, optH * opts.length + 4, 0x0d0a08, 0.98)
          .setStrokeStyle(1, 0xc8973a, 0.9).setDepth(94),
      );
      opts.forEach((key, i) => {
        const oy = dropTop + i * optH + optH / 2;
        const isActive = key === filterHero;
        const optBg = this.add.rectangle(dropX, oy, optW - 4, optH, isActive ? 0x2a1a08 : 0x0d0a08, 1)
          .setDepth(95).setInteractive({ useHandCursor: true });
        const optTx = this.add.text(dropX - optW / 2 + 10, oy, heroLabel(key), {
          fontSize: '10px', fontFamily: 'Georgia, serif',
          color: isActive ? '#f0c040' : '#a07840',
        }).setOrigin(0, 0.5).setDepth(96);
        optBg.on('pointerover', () => optBg.setFillStyle(0x2a1a08, 1));
        optBg.on('pointerout', () => optBg.setFillStyle(isActive ? 0x2a1a08 : 0x0d0a08, 1));
        optBg.on('pointerdown', () => setFilter(key));
        filterDropdownObjects.push(optBg, optTx);
      });
      filterOpen = true;
    };
    heroHeaderBg.on('pointerover', () => heroHeaderTxt.setColor('#c8973a'));
    heroHeaderBg.on('pointerout',  () => heroHeaderTxt.setColor('#8a6420'));
    heroHeaderBg.on('pointerdown', toggleFilter);

    // ── Rank label drawer — simple "#N" text centered in the rank column.
    // Was an aspect-compensated coin/medal but it could clip the header
    // divider in the first row, and the user asked for a cleaner read.
    // Tier coloring preserved so top 3 still get gold/silver/bronze pop;
    // 4-5 copper, 6-10 muted, 11+ dim — long boards visually taper.
    const RANK_TINTS: Array<{ text: string }> = [
      { text: '#fff0a0' }, // 1 — gold
      { text: '#f0f0f0' }, // 2 — silver
      { text: '#ffd0a0' }, // 3 — bronze
      { text: '#d8a868' }, // 4-5 — copper
      { text: '#a07840' }, // 6-10 — muted
      { text: '#6b4818' }, // 11+ — dim
    ];
    const tintForRank = (rank: number): { text: string } => {
      if (rank === 1) return RANK_TINTS[0];
      if (rank === 2) return RANK_TINTS[1];
      if (rank === 3) return RANK_TINTS[2];
      if (rank <= 5)  return RANK_TINTS[3];
      if (rank <= 10) return RANK_TINTS[4];
      return RANK_TINTS[5];
    };
    const drawRankLabel = (rank: number, x: number, y: number): Phaser.GameObjects.GameObject[] => {
      const tint = tintForRank(rank);
      const isTop3 = rank <= 3;
      // Top 3 get a meaningfully larger numeral so the podium pops on the
      // page; ranks 4+ stay compact so a dense board doesn't feel shouty.
      const t = this.add.text(x, y, `#${rank}`, {
        fontSize: isTop3 ? '18px' : '13px',
        fontFamily: "'Cinzel Decorative', Georgia, serif",
        color: tint.text,
        fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(84);
      return [t];
    };
    // Aspect-ratio compensation for round book pills — the canvas runs on
    // separate X / Y camera zoom, so a Phaser ellipse drawn with equal
    // width and height renders as an ellipse on any non-5:3 window. Pre-
    // stretch the X axis by `zoomY / zoomX` so the displayed pill is round.
    const zoomRatio = getZoomY() / Math.max(0.001, getZoomX());

    // Show / hide helpers for the shared bookTip declared above cleanup.
    const showBookTip = (x: number, y: number, name: string, desc: string): void => {
      bookTip?.destroy();
      bookTip = createHudTooltip(this, x, y, `${name}\n${desc}`,
        0.5, 1, { fontSize: '10px', wrap: 220, depth: 99 });
    };
    const hideBookTip = (): void => { bookTip?.destroy(); bookTip = null; };

    const renderRows = (): void => {
      rowObjects.forEach((r) => r.destroy());
      rowObjects.length = 0;

      if (loading) {
        rowObjects.push(this.add.text(cx, cy + 20, 'Loading…', {
          fontSize: '13px', fontFamily: 'Georgia, serif', color: '#5a3c1a',
        }).setOrigin(0.5).setDepth(82));
        return;
      }

      const rawBoard = activeTab === 'standard' ? stdBoard : infBoard;
      const board = filterHero === 'all'
        ? rawBoard
        : rawBoard.filter((e) => (e.heroKey ?? '') === filterHero);
      const isInferno = activeTab === 'inferno';

      if (board.length === 0) {
        const msg = filterHero !== 'all'
          ? `No ${heroLabel(filterHero)} runs on this board yet.`
          : (isInferno
            ? 'No Inferno runs yet — reach Stage 40 to unlock!'
            : 'No scores yet — go fight!');
        rowObjects.push(this.add.text(cx, cy + 20, msg, {
          fontSize: '12px', fontFamily: 'Georgia, serif', color: '#5a3c1a', fontStyle: 'italic',
        }).setOrigin(0.5).setDepth(82));
        return;
      }

      // Sort by the active key/direction before slicing the visible window.
      // Stage maps to `level` on standard boards and `infernoZone` on the
      // inferno board — both are numeric so a single numeric compare works.
      const sorted = [...board].sort((a, b) => {
        let av: number;
        let bv: number;
        if (sortKey === 'score') {
          av = a.score;
          bv = b.score;
        } else {
          av = isInferno ? ((a as any).infernoZone ?? 0) : ((a as any).level ?? 0);
          bv = isInferno ? ((b as any).infernoZone ?? 0) : ((b as any).level ?? 0);
        }
        return sortDir === 'desc' ? bv - av : av - bv;
      });
      const visible = sorted.slice(scrollOffset, scrollOffset + ROWS);
      const ROW_TOP = hY + 32; // first row's centre offset from header (extra air below the divider so rank/portrait don't kiss the line)
      visible.forEach((entry, ri) => {
        const rank = scrollOffset + ri + 1;
        const rowY = ROW_TOP + ri * ROW_H;
        const isTop3 = rank <= 3;
        const rowColor = isTop3
          ? ['#ffe8a0', '#e8e8e8', '#f0c890'][rank - 1]
          : '#a07840';
        // Subtle stripe — every other row gets a faint dark fill so the
        // eye can track across the wider columns without losing its line.
        if (ri % 2 === 1) {
          rowObjects.push(this.add.rectangle(cx, rowY, panelW - 60, ROW_H - 4, 0x1a1208, 0.42).setDepth(81.5));
        }
        // Rank label — "#N" text centred in the rank column
        const label = drawRankLabel(rank, tableLeft + COL_RANK_CX, rowY);
        for (const o of label) rowObjects.push(o);
        // Hero portrait — feet-anchored to the row's bottom edge so every
        // hero shows the same body region regardless of how their idle
        // frame is positioned within the 128×128 spritesheet (some
        // assets sit high in the frame, some low — origin (0.5, 0.5)
        // made them appear at random heights inside the row). Origin
        // (0.5, 1.0) at rowY + ROW_H/2 − 4 keeps every hero's feet on
        // the same baseline; head heights vary naturally with hero
        // height. Display size kept square (36 × 36) but the origin
        // change is the actual fix.
        const heroKey = entry.heroKey ?? null;
        const heroX = tableLeft + COL_HERO_CX;
        const heroFeetY = rowY + ROW_H / 2 - 2;
        if (heroKey && this.textures.exists(`hero_${heroKey}`)) {
          const sprite = this.add.sprite(heroX, heroFeetY, `hero_${heroKey}`)
            .setDisplaySize(36, 36).setOrigin(0.5, 1).setDepth(83);
          rowObjects.push(sprite);
        } else {
          rowObjects.push(this.add.text(heroX, rowY, '—', {
            fontSize: '14px', fontFamily: 'Georgia, serif', color: '#5a3c1a',
          }).setOrigin(0.5).setDepth(83));
        }
        const cellStyle = {
          fontSize: '12px', fontFamily: 'Georgia, serif',
          color: rowColor, fontStyle: isTop3 ? 'bold' : 'normal',
        } as const;
        const scoreStr = entry.score.toLocaleString();
        const zoneStr = isInferno
          ? `I-${(entry as any).infernoZone ?? '?'}`
          : `${(entry as any).level ?? '?'}`;
        // All row data centred under its column header so the table reads
        // as an aligned grid. Names longer than the column band are
        // visually clipped by neighbouring text — entries are typically
        // <12 chars so this is rare in practice.
        rowObjects.push(
          this.add.text(tableLeft + COL_NAME_CX,  rowY, entry.name, cellStyle).setOrigin(0.5, 0.5).setDepth(83),
          this.add.text(tableLeft + COL_SCORE_CX, rowY, scoreStr,   cellStyle).setOrigin(0.5, 0.5).setDepth(83),
          this.add.text(tableLeft + COL_ZONE_CX,  rowY, zoneStr,    cellStyle).setOrigin(0.5, 0.5).setDepth(83),
        );

        // Books column — small coloured rune icons for the run's top tomes.
        // Each icon is a circle pill carrying the tome's glyph in its
        // canonical colour. Hover any icon to see name + description in
        // a HUD tooltip. Up to 5 fit in the column width without bleeding
        // into the panel edge; if the run picked more, a small "+N" tail
        // shows the remainder.
        const tomeIds = entry.tomeIds;
        if (tomeIds && tomeIds.length > 0) {
          const ids = tomeIds.split(',').filter((s) => s.length > 0);
          const visibleN = Math.min(4, ids.length);
          // Wider per-icon slot — 24 px so the bigger pills (20×20)
          // don't kiss their neighbours. Strip still centers around
          // COL_BOOKS_CX, so 1-icon rows sit dead-under the header
          // and 4-icon rows fan out evenly.
          const ICON_W = 24;
          // Center the icon strip around COL_BOOKS_CX so 1-icon runs sit
          // dead-center under the BOOKS header and 4-icon runs fan out
          // evenly. Previously icons left-anchored at COL_BOOKS_LEFT —
          // few-icon rows drifted left of the header, lots-of-icon rows
          // slid right and the column read inconsistently. Tail counts
          // toward the strip width so the "+N" overflow doesn't push
          // the icons off-center either.
          const hasTail = ids.length > visibleN;
          const slotsForCenter = visibleN + (hasTail ? 1 : 0);
          const stripFirstX = COL_BOOKS_CX - ((slotsForCenter - 1) * ICON_W) / 2;
          for (let bi = 0; bi < visibleN; bi++) {
            const id = ids[bi];
            const def = (TOME_DEFINITIONS as Record<string, { name: string; description: string; icon: string; color: string }>)[id];
            const ix = tableLeft + stripFirstX + bi * ICON_W;
            const iy = rowY;
            // Pill background — tinted to the tome's colour at low alpha
            // so the row reads consistently regardless of which tome.
            const pillCol = def ? Phaser.Display.Color.HexStringToColor(def.color).color : 0x5a3c1a;
            // Match the rank-coin aspect compensation so book icons stay
            // round on wide / tall windows. `Phaser.GameObjects.Ellipse`
            // accepts width/height so we can pre-stretch X by zoomRatio.
            const pill = this.add.ellipse(ix, iy, 20 * zoomRatio, 20, pillCol, 0.20)
              .setStrokeStyle(1.5, pillCol, 0.85).setDepth(83)
              .setInteractive({ useHandCursor: true });
            const glyph = this.add.text(ix, iy, def?.icon ?? '?', {
              fontSize: '14px', fontFamily: 'Georgia, serif',
              color: def?.color ?? '#a07840', fontStyle: 'bold',
            }).setOrigin(0.5).setDepth(84);
            const tipName = def?.name ?? id;
            const tipDesc = def?.description ?? '';
            pill.on('pointerover', () => {
              pill.setFillStyle(pillCol, 0.32);
              showBookTip(ix, iy - 14, tipName, tipDesc);
            });
            pill.on('pointerout', () => {
              pill.setFillStyle(pillCol, 0.18);
              hideBookTip();
            });
            rowObjects.push(pill, glyph);
          }
          // Overflow tail: "+3" when the run had more picks than fit.
          // Sits in the slot reserved for it by stripFirstX (centered
          // origin so it visually balances with the icon row).
          if (hasTail) {
            const tailX = tableLeft + stripFirstX + visibleN * ICON_W;
            rowObjects.push(this.add.text(tailX, rowY, `+${ids.length - visibleN}`, {
              fontSize: '10px', fontFamily: 'Georgia, serif',
              color: isTop3 ? rowColor : '#a07840', fontStyle: 'italic',
            }).setOrigin(0.5, 0.5).setDepth(83));
          }
        } else {
          rowObjects.push(this.add.text(tableLeft + COL_BOOKS_CX, rowY, '—', {
            fontSize: '12px', fontFamily: 'Georgia, serif', color: '#5a3c1a',
          }).setOrigin(0.5, 0.5).setDepth(83));
        }
      });

      if (board.length > ROWS) {
        rowObjects.push(this.add.text(cx, cy + panelH / 2 - 24,
          `${scrollOffset + 1}–${Math.min(scrollOffset + ROWS, board.length)} of ${board.length}  ·  scroll for more`, {
          fontSize: '10px', fontFamily: 'Georgia, serif', color: '#5a3c1a', fontStyle: 'italic',
        }).setOrigin(0.5).setDepth(82));
      }
    };

    // Scroll buttons — anchored to the right side of the row band
    const upBtn = this.add.text(cx + panelW / 2 - 18, cy - 60, '▲', {
      fontSize: '13px', fontFamily: 'Arial', color: '#5c4f3a',
    }).setDepth(83).setInteractive({ useHandCursor: true });
    upBtn.on('pointerdown', () => { scrollOffset = Math.max(0, scrollOffset - 1); renderRows(); });
    upBtn.on('pointerover', () => upBtn.setColor('#c8973a'));
    upBtn.on('pointerout',  () => upBtn.setColor('#5c4f3a'));
    elements.push(upBtn);
    const downBtn = this.add.text(cx + panelW / 2 - 18, cy + 100, '▼', {
      fontSize: '13px', fontFamily: 'Arial', color: '#5c4f3a',
    }).setDepth(83).setInteractive({ useHandCursor: true });
    downBtn.on('pointerdown', () => {
      const board = activeTab === 'standard' ? stdBoard : infBoard;
      if (scrollOffset + ROWS < board.length) { scrollOffset++; renderRows(); }
    });
    downBtn.on('pointerover', () => downBtn.setColor('#c8973a'));
    downBtn.on('pointerout',  () => downBtn.setColor('#5c4f3a'));
    elements.push(downBtn);

    const wheelHandler = (_ptr: unknown, _gos: unknown, _dx: unknown, dy: number): void => {
      const rawBoard = activeTab === 'standard' ? stdBoard : infBoard;
      const board = filterHero === 'all'
        ? rawBoard
        : rawBoard.filter((e) => (e.heroKey ?? '') === filterHero);
      if (dy > 0 && scrollOffset + ROWS < board.length) { scrollOffset++; renderRows(); }
      else if (dy < 0) { scrollOffset = Math.max(0, scrollOffset - 1); renderRows(); }
    };
    this.input.on('wheel', wheelHandler);

    // No overlay-click-to-close — user reported accidentally dismissing
    // the leaderboard while interacting with rows / sort headers / book
    // tooltips. Closing is now strictly via the ✕ button (and ESC).
    this.input.keyboard?.once('keydown-ESC', cleanup);
    renderRows();

    // Fetch global leaderboard from server
    const apiBase = (import.meta as any).env?.VITE_API_URL || (typeof window !== 'undefined' && window.location?.hostname !== 'localhost' ? '' : 'http://localhost:4000');
    Promise.all([
      fetch(`${apiBase}/api/leaderboard?mode=standard`).then((r) => r.ok ? r.json() : null).catch(() => null),
      fetch(`${apiBase}/api/leaderboard?mode=inferno`).then((r) => r.ok ? r.json() : null).catch(() => null),
    ]).then(([stdData, infData]) => {
      if (stdData?.entries) stdBoard = stdData.entries;
      if (infData?.entries) infBoard = infData.entries;
      loading = false;
      renderRows();
    });
  }

  /** Confirmation popup before entering Inferno Mode */
  private showInfernoConfirm(): void {
    const cx = 480, cy = 288;
    const els: Phaser.GameObjects.GameObject[] = [];
    const cleanup = () => els.forEach((o) => o.destroy());

    els.push(this.add.rectangle(cx, cy, 960, 576, 0x000000, 0.70).setDepth(80).setInteractive());
    els.push(this.add.rectangle(cx, cy, 360, 190, 0x0d0402, 0.99).setStrokeStyle(2, 0xcc2200).setDepth(81));
    els.push(this.add.rectangle(cx, cy, 354, 184, 0, 0).setStrokeStyle(1, 0xff4020, 0.25).setDepth(81));

    els.push(this.add.text(cx, cy - 68, 'ENTER INFERNO?', {
      fontSize: '16px', fontFamily: 'Georgia, serif', color: '#cc2200', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(82));

    els.push(this.add.text(cx, cy - 40, 'Say goodbye to your recruits.', {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#a07840', fontStyle: 'italic',
    }).setOrigin(0.5).setDepth(82));

    els.push(this.add.text(cx, cy - 18, 'Only your first hero enters.\nAny items gained are lost if you quit.', {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: '#6b4020', align: 'center',
    }).setOrigin(0.5).setDepth(82));

    // YES button
    const yesBg = this.add.rectangle(cx - 58, cy + 38, 90, 28, 0x1e0404, 1)
      .setStrokeStyle(1, 0x8b1a00).setDepth(82).setInteractive({ useHandCursor: true });
    const yesTxt = this.add.text(cx - 58, cy + 38, 'ENTER', {
      fontSize: '12px', fontFamily: 'Georgia, serif', color: '#cc2200', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(83).setInteractive({ useHandCursor: true });
    yesBg.on('pointerover', () => { yesBg.setFillStyle(0x2e0808); yesTxt.setColor('#ff4020'); });
    yesBg.on('pointerout',  () => { yesBg.setFillStyle(0x1e0404); yesTxt.setColor('#cc2200'); });
    const doEnter = () => {
      cleanup();
      this.gsm.startInfernoRun();
      this.stopMusic();
      this.scene.start('BattleScene', { inferno: true });
    };
    yesBg.on('pointerdown', doEnter);
    yesTxt.on('pointerdown', doEnter);
    els.push(yesBg, yesTxt);

    // NO button
    const noBg = this.add.rectangle(cx + 58, cy + 38, 90, 28, 0x0d0a08, 1)
      .setStrokeStyle(1, 0x5a3600).setDepth(82).setInteractive({ useHandCursor: true });
    const noTxt = this.add.text(cx + 58, cy + 38, 'RETREAT', {
      fontSize: '12px', fontFamily: 'Georgia, serif', color: '#a07840', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(83).setInteractive({ useHandCursor: true });
    noBg.on('pointerover', () => { noBg.setStrokeStyle(1, 0x9a6020); noTxt.setColor('#c8973a'); });
    noBg.on('pointerout',  () => { noBg.setStrokeStyle(1, 0x5a3600); noTxt.setColor('#a07840'); });
    noBg.on('pointerdown', cleanup);
    noTxt.on('pointerdown', cleanup);
    els.push(noBg, noTxt);
  }

  /** Brief notice shown after F5/tab-close restores pre-inferno state */
  private showInfernoAbandonNotice(): void {
    const msg = this.add.text(480, 545, 'Inferno run abandoned — hero restored to pre-inferno state', {
      fontSize: '10px', fontFamily: 'Georgia, serif', color: '#cc2200',
      backgroundColor: '#0d0402', padding: { x: 10, y: 5 },
      stroke: '#5a1a00', strokeThickness: 1,
    }).setOrigin(0.5).setDepth(100);
    this.tweens.add({ targets: msg, alpha: 0, delay: 3500, duration: 600, onComplete: () => msg.destroy() });
  }

  private confirmDeleteSlot(slot: number, _cx: number, _cy: number): void {
    const overlay = this.add.rectangle(480, 288, 960, 576, 0x000000, 0.65).setDepth(50).setInteractive();
    // Taller panel to host a "Remove equipped Sigils" action row above DELETE/CANCEL.
    const panel = this.add.rectangle(480, 288, 360, 196, 0x0d0a08, 0.98).setStrokeStyle(2, 0x8b2000).setDepth(51);
    const innerBorder = this.add.rectangle(480, 288, 354, 190, 0x0d0a08, 0).setStrokeStyle(1, 0xc8973a, 0.2).setDepth(51);

    const equippedCount = this.gsm.equippedSigilCountForSlot(slot);
    const msg = this.add.text(480, 240, `Delete Slot ${slot}?\nThis cannot be undone.`, {
      fontSize: '12px', fontFamily: 'Georgia, serif', color: '#c84040', align: 'center',
    }).setOrigin(0.5).setDepth(52);

    // Optional pre-delete action: unequip all sigils for this slot. Account collection is untouched.
    const sigilCountTxt = this.add.text(480, 282,
      equippedCount > 0
        ? `${equippedCount} equipped sigil${equippedCount === 1 ? '' : 's'} on this slot`
        : 'No equipped sigils on this slot',
      {
        fontSize: '10px', fontFamily: 'Georgia, serif',
        color: equippedCount > 0 ? '#c8973a' : '#5a3600',
        align: 'center',
      },
    ).setOrigin(0.5).setDepth(52);

    const unequipBtnBg = this.add.rectangle(480, 308, 180, 26, 0x1a0f06, 1)
      .setStrokeStyle(1, equippedCount > 0 ? 0xc8973a : 0x3a2a18).setDepth(52);
    const unequipBtn = this.add.text(480, 308, 'Remove equipped Sigils', {
      fontSize: '10px', fontFamily: 'Georgia, serif',
      color: equippedCount > 0 ? '#c8973a' : '#5a3600', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(53);
    if (equippedCount > 0) {
      unequipBtn.setInteractive({ useHandCursor: true });
      unequipBtnBg.setInteractive({ useHandCursor: true });
      const doUnequip = () => {
        this.gsm.unequipAllSigilsForSlot(slot);
        sigilCountTxt.setText('No equipped sigils on this slot');
        sigilCountTxt.setColor('#5a3600');
        unequipBtn.setColor('#5a3600');
        unequipBtnBg.setStrokeStyle(1, 0x3a2a18);
        unequipBtn.disableInteractive();
        unequipBtnBg.disableInteractive();
      };
      unequipBtn.on('pointerdown', doUnequip);
      unequipBtnBg.on('pointerdown', doUnequip);
    }

    // Both the rectangle background AND the text label get made interactive — previously
    // only the text was interactive, so a click anywhere inside the 100×32 button rectangle
    // but outside the narrow text hitbox fell through to the overlay (which cancels),
    // making DELETE look like it did nothing.
    const yesBtnBg = this.add.rectangle(424, 348, 100, 32, 0x1a0608, 1).setStrokeStyle(2, 0x8b2000).setDepth(52).setInteractive({ useHandCursor: true });
    const yesBtn = this.add.text(424, 348, 'DELETE', {
      fontSize: '12px', fontFamily: 'Georgia, serif', color: '#e8a030', fontStyle: 'bold',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setDepth(53);
    const yesOver = () => { yesBtn.setColor('#ffe8a0'); yesBtnBg.setStrokeStyle(2, 0xc84000); };
    const yesOut  = () => { yesBtn.setColor('#e8a030'); yesBtnBg.setStrokeStyle(2, 0x8b2000); };
    yesBtn.on('pointerover', yesOver).on('pointerout', yesOut);
    yesBtnBg.on('pointerover', yesOver).on('pointerout', yesOut);

    const noBtnBg = this.add.rectangle(536, 348, 100, 32, 0x0d0a08, 1).setStrokeStyle(2, 0x5a3600).setDepth(52).setInteractive({ useHandCursor: true });
    const noBtn = this.add.text(536, 348, 'CANCEL', {
      fontSize: '12px', fontFamily: 'Georgia, serif', color: '#a07840',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setDepth(53);
    const noOver = () => { noBtn.setColor('#c8973a'); noBtnBg.setStrokeStyle(2, 0x9a6020); };
    const noOut  = () => { noBtn.setColor('#a07840'); noBtnBg.setStrokeStyle(2, 0x5a3600); };
    noBtn.on('pointerover', noOver).on('pointerout', noOut);
    noBtnBg.on('pointerover', noOver).on('pointerout', noOut);

    const cleanup = () => [
      overlay, panel, innerBorder, msg, sigilCountTxt,
      unequipBtnBg, unequipBtn, yesBtnBg, yesBtn, noBtnBg, noBtn,
    ].forEach((o) => o.destroy());

    const doDelete = () => { this.gsm.deleteSaveSlot(slot); cleanup(); this.scene.restart(); };
    const doCancel = () => cleanup();
    yesBtn.on('pointerdown', doDelete);
    yesBtnBg.on('pointerdown', doDelete);
    noBtn.on('pointerdown', doCancel);
    noBtnBg.on('pointerdown', doCancel);
    overlay.on('pointerdown', doCancel);
  }

  /** PvP / Co-op mode picker — opened from the Multiplayer nav grid tile.
   *  Modal with two big cards (PVP red, Co-op green); picking a mode
   *  transitions directly into the matching lobby, pre-filled with the
   *  current save slot (no in-lobby save picker anymore). */
  public showMultiplayerSelect(): void {
    const currentSlot = this.gsm.player.saveSlot;
    const overlay = this.add.rectangle(480, 288, 960, 576, 0x000000, 0.7).setDepth(50).setInteractive();
    const panel = this.add.rectangle(480, 288, 560, 300, 0x0d0a08, 0.98).setStrokeStyle(2, 0x5a3600).setDepth(51);
    const innerBorder = this.add.rectangle(480, 288, 554, 294, 0x0d0a08, 0).setStrokeStyle(1, 0xc8973a, 0.2).setDepth(51);
    const titleTxt = this.add.text(480, 180, '✦  Multiplayer  ✦', {
      fontSize: '18px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(52);

    const elements: Phaser.GameObjects.GameObject[] = [overlay, panel, innerBorder, titleTxt];
    const cleanup = (): void => elements.forEach((o) => o.destroy());

    // Two mode cards, 220×220, centered horizontally on the 560-wide panel.
    const makeModeCard = (
      cx: number, label: string, caption: string, icon: string,
      primaryColor: number, onClick: () => void,
    ): void => {
      const cardY = 288;
      const bg = this.add.rectangle(cx, cardY, 220, 220, 0x1a1208, 0.98)
        .setStrokeStyle(2, primaryColor).setDepth(52)
        .setInteractive({ useHandCursor: true });
      const innerAcc = this.add.rectangle(cx, cardY, 214, 214, 0, 0)
        .setStrokeStyle(1, primaryColor, 0.3).setDepth(52);
      const iconTxt = this.add.text(cx, cardY - 50, icon, {
        fontSize: '48px', fontFamily: 'Georgia, serif', color: '#d4a855',
      }).setOrigin(0.5).setDepth(53);
      const labelTxt = this.add.text(cx, cardY + 20, label, {
        fontSize: '22px', fontFamily: 'Georgia, serif', color: '#d4a855', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(53).setInteractive({ useHandCursor: true });
      const capTxt = this.add.text(cx, cardY + 52, caption, {
        fontSize: '11px', fontFamily: 'Georgia, serif', color: '#8a6030', align: 'center',
        wordWrap: { width: 200 },
      }).setOrigin(0.5).setDepth(53);
      const hoverColor = Math.min(0xffffff, primaryColor + 0x202010);
      bg.on('pointerover', () => {
        labelTxt.setColor('#ffe8a0');
        iconTxt.setColor('#ffe8a0');
        bg.setStrokeStyle(3, hoverColor);
      });
      bg.on('pointerout', () => {
        labelTxt.setColor('#d4a855');
        iconTxt.setColor('#d4a855');
        bg.setStrokeStyle(2, primaryColor);
      });
      const fire = (): void => { cleanup(); onClick(); };
      bg.on('pointerdown', fire);
      labelTxt.on('pointerdown', fire);
      elements.push(bg, innerAcc, iconTxt, labelTxt, capTxt);
    };

    makeModeCard(360, 'PvP', 'Ranked 1v1\nElo matchmaking', '⚔', 0x8b2000,
      () => this.scene.start('PvpLobbyScene', { saveSlot: currentSlot }));
    makeModeCard(600, 'Co-op', 'Team up vs monsters\n2 players', '🤝', 0x5a7030,
      () => this.scene.start('CoopLobbyScene', { saveSlot: currentSlot }));

    // Bronze close-X — matches every other scene / popup. Was red
    // previously, which made the mode picker the second outlier in the
    // close-X language alongside the leaderboard (also normalized).
    const closeTxt = this.add.text(745, 150, '✕', {
      fontSize: '14px', fontFamily: 'Georgia, serif', color: '#5a3600',
    }).setOrigin(0.5).setDepth(53).setInteractive({ useHandCursor: true });
    closeTxt.on('pointerover', () => closeTxt.setColor('#c8973a'));
    closeTxt.on('pointerout', () => closeTxt.setColor('#5a3600'));
    closeTxt.on('pointerdown', () => cleanup());
    elements.push(closeTxt);

    overlay.on('pointerdown', () => cleanup());
  }

  private showNewJourneyConfirm(): void {
    const slot = this.gsm.player.saveSlot;
    const overlay = this.add.rectangle(480, 288, 960, 576, 0x000000, 0.65).setDepth(50).setInteractive();
    const panel = this.add.rectangle(480, 288, 380, 170, 0x0d0a08, 0.98).setStrokeStyle(2, 0x5a3600).setDepth(51);
    const innerBorder = this.add.rectangle(480, 288, 374, 164, 0x0d0a08, 0).setStrokeStyle(1, 0xc8973a, 0.2).setDepth(51);
    const titleTxt = this.add.text(480, 248, '✦  Begin New Journey  ✦', {
      fontSize: '15px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(52);
    const warnTxt = this.add.text(480, 275, `Slot ${slot} will be wiped.\nAll heroes, loot and progress will be lost.\n◆ Runes and high scores are kept.`, {
      fontSize: '10px', fontFamily: 'Georgia, serif', color: '#8a6030', align: 'center',
    }).setOrigin(0.5).setDepth(52);

    const yesBtnBg = this.add.rectangle(424, 322, 130, 32, 0x1a0608, 1).setStrokeStyle(2, 0x8b2000).setDepth(52);
    const yesBtn = this.add.text(424, 322, 'START FRESH', {
      fontSize: '12px', fontFamily: 'Georgia, serif', color: '#e8a030', fontStyle: 'bold',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setDepth(53);
    yesBtn.on('pointerover', () => { yesBtn.setColor('#ffe8a0'); yesBtnBg.setStrokeStyle(2, 0xc84000); });
    yesBtn.on('pointerout', () => { yesBtn.setColor('#e8a030'); yesBtnBg.setStrokeStyle(2, 0x8b2000); });

    const noBtnBg = this.add.rectangle(544, 322, 110, 32, 0x0d0a08, 1).setStrokeStyle(2, 0x5a3600).setDepth(52);
    const noBtn = this.add.text(544, 322, 'CANCEL', {
      fontSize: '12px', fontFamily: 'Georgia, serif', color: '#a07840',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setDepth(53);
    noBtn.on('pointerover', () => { noBtn.setColor('#c8973a'); noBtnBg.setStrokeStyle(2, 0x9a6020); });
    noBtn.on('pointerout', () => { noBtn.setColor('#a07840'); noBtnBg.setStrokeStyle(2, 0x5a3600); });

    const elements = [overlay, panel, innerBorder, titleTxt, warnTxt, yesBtnBg, yesBtn, noBtnBg, noBtn];
    const cleanup = () => elements.forEach((o) => o.destroy());

    yesBtn.on('pointerdown', () => { cleanup(); this.gsm.resetSave(); this.scene.start('HeroSelectScene'); });
    noBtn.on('pointerdown', () => cleanup());
    overlay.on('pointerdown', () => cleanup());
  }

  /** Popup shown when the player clicks to equip a collection sigil on a
   *  fresh save slot (stage 1 / wave 0 / no stays). Three choices:
   *  1. Insure (pay SIGIL_INSURANCE_PRICE runes) — sigil moves `collection →
   *     bag` with `insured: true`. It'll return to the collection on death.
   *  2. Equip uninsured — sigil stays in collection and just gets `equipSigil`'d.
   *     Safe from combat (not in bag) but the player made a conscious choice
   *     not to spend the insurance runes.
   *  3. Cancel — nothing happens.
   *  `onCommit()` is called when either Insure or Uninsured commits so the
   *  caller can re-render its collection + slot panels. */
  /** Popup when the player tries to equip a collection sigil during an
   *  active run (waveNumber >= 1, past stage 1, or any stay). Single OK
   *  button — purely informational, no action possible here.
   *
   *  Depth band starts at D=200 so the popup sits cleanly above the
   *  sigil collection grid (which uses DEPTH=80 for cells and DEPTH+5/+6
   *  for badges). Previously the popup rendered at depth 50-53 which
   *  put it BEHIND the interactive grid cells — clicks on OK landed on
   *  invisible sigil hitboxes instead, and the user reported a "black
   *  screen" (the popup's overlay rendered under the grid icons) plus a
   *  3-click dismiss. */
  private showSigilLockedPopup(message: string): void {
    const D = 200;
    const overlay = this.add.rectangle(480, 288, 960, 576, 0x000000, 0.70).setDepth(D).setInteractive();
    const panel = this.add.rectangle(480, 288, 380, 200, 0x0d0a08, 0.98).setStrokeStyle(2, 0x7a2010).setDepth(D + 1);
    const innerBorder = this.add.rectangle(480, 288, 374, 194, 0x0d0a08, 0).setStrokeStyle(1, 0xcc3322, 0.25).setDepth(D + 1);
    // Close-X — matches the pattern used by every other popup / scene
    // (bronze resting, gold hover). Red theme on the panel is preserved
    // because this is a "locked / forbidden" message, but the close-X
    // itself stays in the standard bronze palette so it reads as the
    // same control everywhere.
    const closeBtn = this.add.text(480 + 380 / 2 - 16, 288 - 200 / 2 + 14, '✕', {
      fontSize: '14px', fontFamily: 'Georgia, serif', color: '#5a3600',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setDepth(D + 3);
    closeBtn.on('pointerover', () => closeBtn.setColor('#c8973a'));
    closeBtn.on('pointerout',  () => closeBtn.setColor('#5a3600'));
    const titleTxt = this.add.text(480, 232, '✦  Sigil Locked  ✦', {
      fontSize: '14px', fontFamily: 'Georgia, serif', color: '#ef6a4a', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(D + 2);
    const bodyTxt = this.add.text(480, 288, message, {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#a07840', align: 'center', lineSpacing: 4,
    }).setOrigin(0.5).setDepth(D + 2);

    // OK button — non-bold weight per user feedback ("text in bold is
    // not looking good"). Subtle bronze fill matches other popup
    // buttons; loses the aggressive red border the locked-popup used
    // to have, since the panel border + title already carry the
    // "warning" tone.
    const okBtnBg = this.add.rectangle(480, 358, 100, 28, 0x140a06, 1).setStrokeStyle(1, 0x5a3600).setDepth(D + 2);
    const okBtn = this.add.text(480, 358, 'OK', {
      fontSize: '12px', fontFamily: 'Georgia, serif', color: '#c8973a',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setDepth(D + 3);
    okBtn.on('pointerover', () => { okBtn.setColor('#ffe8a0'); okBtnBg.setStrokeStyle(1, 0xc8973a); });
    okBtn.on('pointerout',  () => { okBtn.setColor('#c8973a'); okBtnBg.setStrokeStyle(1, 0x5a3600); });

    const els = [overlay, panel, innerBorder, closeBtn, titleTxt, bodyTxt, okBtnBg, okBtn];
    const cleanup = () => els.forEach((o) => o.destroy());
    okBtn.on('pointerdown', cleanup);
    closeBtn.on('pointerdown', cleanup);
    overlay.on('pointerdown', cleanup);
  }

  private showSigilInsurancePrompt(sigilId: string, sigilName: string, onCommit: () => void): void {
    const cost = GameStateManager.SIGIL_INSURANCE_PRICE;
    const runes = this.gsm.player.resources.loot ?? 0;
    const canAfford = runes >= cost;

    // Depth: must sit ABOVE the Sigil Vendor's DEPTH=80 + offsets (sigil
    // icons render at ~83). User report: insurance prompt was rendering
    // under the vendor's sigil row, leading to the "INSURE" button visually
    // overlapping the icons. Use 200+ band so nothing in the vendor stack
    // can poke through.
    const D = 200;
    const overlay = this.add.rectangle(480, 288, 960, 576, 0x000000, 0.75).setDepth(D).setInteractive();
    const panel = this.add.rectangle(480, 288, 420, 200, 0x0d0a08, 0.98).setStrokeStyle(2, 0x5a3600).setDepth(D + 1);
    const innerBorder = this.add.rectangle(480, 288, 414, 194, 0x0d0a08, 0).setStrokeStyle(1, 0xc8973a, 0.2).setDepth(D + 1);
    const titleTxt = this.add.text(480, 230, '✦  Insure This Sigil?  ✦', {
      fontSize: '14px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(D + 2);
    const bodyTxt = this.add.text(480, 272, `${sigilName}\n\nInsured sigils return to your collection\nif you die. Uninsured collection sigils\nstay safe but can't be recovered if moved.`, {
      fontSize: '10px', fontFamily: 'Georgia, serif', color: '#8a6030', align: 'center', lineSpacing: 3,
    }).setOrigin(0.5).setDepth(D + 2);

    const insureColor = canAfford ? 0x8b2000 : 0x4a1800;
    const insureBtnBg = this.add.rectangle(395, 345, 150, 32, 0x1a0608, 1).setStrokeStyle(2, insureColor).setDepth(D + 2);
    const insureBtn = this.add.text(395, 345, `INSURE  ${cost} ◆`, {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: canAfford ? '#e8a030' : '#5a3000', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(D + 3);
    if (canAfford) {
      insureBtn.setInteractive({ useHandCursor: true });
      insureBtn.on('pointerover', () => { insureBtn.setColor('#ffe8a0'); insureBtnBg.setStrokeStyle(2, 0xc84000); });
      insureBtn.on('pointerout',  () => { insureBtn.setColor('#e8a030'); insureBtnBg.setStrokeStyle(2, 0x8b2000); });
    }

    const noInsureBtnBg = this.add.rectangle(555, 345, 150, 32, 0x0d0a08, 1).setStrokeStyle(2, 0x5a3600).setDepth(D + 2);
    const noInsureBtn = this.add.text(555, 345, 'EQUIP UNINSURED', {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#a07840',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setDepth(D + 3);
    noInsureBtn.on('pointerover', () => { noInsureBtn.setColor('#c8973a'); noInsureBtnBg.setStrokeStyle(2, 0x9a6020); });
    noInsureBtn.on('pointerout',  () => { noInsureBtn.setColor('#a07840'); noInsureBtnBg.setStrokeStyle(2, 0x5a3600); });

    const cancelTxt = this.add.text(480, 378, '— Cancel —', {
      fontSize: '10px', fontFamily: 'Georgia, serif', color: '#4a3010', fontStyle: 'italic',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setDepth(D + 3);
    cancelTxt.on('pointerover', () => cancelTxt.setColor('#7a5020'));
    cancelTxt.on('pointerout',  () => cancelTxt.setColor('#4a3010'));

    const els = [overlay, panel, innerBorder, titleTxt, bodyTxt,
      insureBtnBg, insureBtn, noInsureBtnBg, noInsureBtn, cancelTxt];
    const cleanup = () => els.forEach((o) => o.destroy());

    if (canAfford) {
      insureBtn.on('pointerdown', () => {
        cleanup();
        if (this.gsm.bringCollectionSigilToBag(sigilId, true)) {
          // Sigil is now in the bag — equipSigil picks it up from there.
          this.gsm.equipSigil(sigilId);
          onCommit();
        }
      });
    }
    noInsureBtn.on('pointerdown', () => {
      cleanup();
      this.gsm.equipSigil(sigilId);
      onCommit();
    });
    cancelTxt.on('pointerdown', () => cleanup());
    overlay.on('pointerdown', () => cleanup());
  }

  private showAuthModal(mode: 'login' | 'register'): void {
    // Pure HTML overlay — avoids Phaser DOM coordinate mismatch on scaled canvases
    const container = (document.getElementById('game-container') ?? document.body) as HTMLElement;
    const titleLabel = mode === 'login' ? '✦  Enter Your Realm  ✦' : '✦  Forge Your Legend  ✦';
    const submitLabel = mode === 'login' ? '⚔ Login' : '✦ Register';
    const autocomplete = mode === 'login' ? 'current-password' : 'new-password';

    const usernameRow = mode === 'register' ? `
        <div style="color:#6b4020;font-size:10px;letter-spacing:1px;margin-bottom:5px;">USERNAME <span style="color:#5a3600;font-size:9px;">(required)</span></div>
        <input id="aun" type="text" placeholder="Your pseudo" autocomplete="username" maxlength="24"
          style="width:100%;box-sizing:border-box;background:#110e08;border:1px solid #5a3600;color:#c8973a;
                 padding:8px 12px;font-family:Georgia,serif;font-size:13px;outline:none;margin-bottom:14px;display:block;"/>` : '';

    const modal = document.createElement('div');
    modal.style.cssText = 'position:fixed;inset:0;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.86);z-index:9999;';
    modal.innerHTML = `
      <div style="background:#0d0a0e;border:2px solid #5a3600;padding:28px 32px;width:320px;box-sizing:border-box;font-family:Georgia,serif;">
        <div style="text-align:center;color:#c8973a;font-size:15px;font-weight:bold;margin-bottom:18px;letter-spacing:1px;">${titleLabel}</div>
        ${usernameRow}
        <div style="color:#6b4020;font-size:10px;letter-spacing:1px;margin-bottom:5px;">EMAIL</div>
        <input id="ae" type="email" placeholder="your@email.com" autocomplete="email"
          style="width:100%;box-sizing:border-box;background:#110e08;border:1px solid #5a3600;color:#c8973a;
                 padding:8px 12px;font-family:Georgia,serif;font-size:13px;outline:none;margin-bottom:14px;display:block;"/>
        <div style="color:#6b4020;font-size:10px;letter-spacing:1px;margin-bottom:5px;">PASSWORD</div>
        <input id="ap" type="password" placeholder="••••••••" autocomplete="${autocomplete}"
          style="width:100%;box-sizing:border-box;background:#110e08;border:1px solid #5a3600;color:#c8973a;
                 padding:8px 12px;font-family:Georgia,serif;font-size:13px;outline:none;margin-bottom:10px;display:block;letter-spacing:3px;"/>
        <div id="aerr" style="color:#c0392b;font-family:Georgia,serif;font-size:10px;min-height:14px;text-align:center;margin-bottom:12px;"></div>
        <div style="display:flex;gap:10px;justify-content:center;margin-bottom:10px;">
          <button id="asub" style="background:#1a0f06;border:1px solid #5a3600;color:#c8973a;font-family:Georgia,serif;font-size:12px;
                  font-weight:bold;padding:8px 22px;cursor:pointer;">${submitLabel}</button>
        </div>
        <div style="text-align:center;">
          <span id="acan" style="color:#4a3010;font-family:Georgia,serif;font-size:10px;cursor:pointer;">— Cancel —</span>
        </div>
      </div>`;

    container.appendChild(modal);

    // Prevent pointer events from reaching the Phaser canvas beneath
    modal.addEventListener('pointerdown', (e) => e.stopPropagation());

    ((modal.querySelector('#aun') ?? modal.querySelector('#ae')) as HTMLInputElement).focus();

    const err = modal.querySelector('#aerr') as HTMLElement;
    const setError = (msg: string) => { err.textContent = msg; };
    const cleanup = () => modal.remove();

    modal.querySelector('#asub')!.addEventListener('click', async () => {
      const email = (modal.querySelector('#ae') as HTMLInputElement).value.trim();
      const pass  = (modal.querySelector('#ap') as HTMLInputElement).value;
      if (!email || !pass) { setError('Email and password are required'); return; }
      try {
        const auth = AuthService.getInstance();
        if (mode === 'login') {
          await auth.login(email, pass);
        } else {
          const username = (modal.querySelector('#aun') as HTMLInputElement)?.value.trim() ?? '';
          if (!username || username.length < 2) { setError('Username is required (min 2 characters)'); return; }
          await auth.register(email, pass, username);
        }
        cleanup(); this.scene.restart();
      } catch (e: unknown) { setError(e instanceof Error ? e.message : 'An error occurred'); }
    });
    modal.querySelector('#acan')!.addEventListener('click', cleanup);
    modal.addEventListener('click', (e) => { if (e.target === modal) cleanup(); });
  }

  private showAccountMenu(): void {
    // Pure HTML overlay — same approach as showAuthModal, avoids canvas coordinate mapping
    const auth = AuthService.getInstance();
    const user = auth.getUser();
    const container = (document.getElementById('game-container') ?? document.body) as HTMLElement;
    const emailLabel = user?.email ?? (user?.walletAddress ? user.walletAddress.slice(0, 12) + '…' : 'Account');
    const currentUsername = user?.username ?? '';

    const modal = document.createElement('div');
    modal.style.cssText = 'position:fixed;inset:0;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.86);z-index:9999;';
    modal.innerHTML = `
      <div style="background:#0d0a0e;border:2px solid #5a3600;padding:28px 32px;width:320px;box-sizing:border-box;font-family:Georgia,serif;">
        <div style="text-align:center;color:#c8973a;font-size:15px;font-weight:bold;margin-bottom:14px;letter-spacing:1px;">✦  Account  ✦</div>
        <div style="text-align:center;color:#6b5020;font-size:11px;margin-bottom:12px;border-bottom:1px solid #3a2400;padding-bottom:12px;">📧 ${emailLabel}</div>
        ${currentUsername ? `<div style="text-align:center;color:#c8973a;font-size:13px;font-family:Georgia,serif;margin-bottom:18px;">⚔ ${currentUsername}</div>` : ''}
        <div style="display:flex;gap:10px;justify-content:center;margin-bottom:12px;">
          <button id="acct-logout" style="background:#1a0606;border:1px solid #7a1a1a;color:#c05050;font-family:Georgia,serif;font-size:12px;
                  font-weight:bold;padding:8px 20px;cursor:pointer;">⚔ Logout</button>
        </div>
        <div style="text-align:center;">
          <span id="acct-close" style="color:#4a3010;font-family:Georgia,serif;font-size:10px;cursor:pointer;">— Close —</span>
        </div>
      </div>`;

    container.appendChild(modal);

    // Prevent pointer events from reaching the Phaser canvas beneath
    modal.addEventListener('pointerdown', (e) => e.stopPropagation());

    const cleanup = () => modal.remove();

    modal.querySelector('#acct-logout')!.addEventListener('click', (e) => { e.stopPropagation(); auth.logout(); cleanup(); this.scene.start('LoginScene'); });
    modal.querySelector('#acct-close')!.addEventListener('click', (e) => { e.stopPropagation(); cleanup(); });
    modal.addEventListener('click', (e) => { if (e.target === modal) cleanup(); });
  }

  private createMenuButton(x: number, y: number, label: string, isPrimary: boolean, onClick: () => void, width = 240): void {
    const H = 38;

    // Background stone
    const bg = this.add.rectangle(x, y, width, H, isPrimary ? 0x1a0608 : 0x0d0a08, 0.92)
      .setInteractive({ useHandCursor: true });

    // Border lines — top and bottom only (bronze metal tablet feel)
    // Primary uses a warm copper-bronze; secondary uses deep dark bronze (matches the darker bronze swatch).
    const borderColor = isPrimary ? 0x8a4a28 : 0x5a3820;
    const topLine = this.add.rectangle(x, y - H / 2 + 1, width - 10, 1, borderColor, 0.85);
    const botLine = this.add.rectangle(x, y + H / 2 - 1, width - 10, 1, borderColor, 0.85);

    // Corner diamond ornaments — muted bronze
    const ornColor = isPrimary ? '#6e3818' : '#5a3820';
    const ornL = this.add.text(x - width / 2 + 10, y, '◆', { fontSize: '9px', color: ornColor }).setOrigin(0.5).setAlpha(0.9);
    const ornR = this.add.text(x + width / 2 - 10, y, '◆', { fontSize: '9px', color: ornColor }).setOrigin(0.5).setAlpha(0.9);

    // Label — original gold palette (bronze was too bright for text)
    const txtColor = isPrimary ? '#e8a030' : '#a07840';
    const txt = this.add.text(x, y, label, {
      fontSize: isPrimary ? '14px' : '12px',
      fontFamily: 'Georgia, serif',
      color: txtColor,
      fontStyle: isPrimary ? 'bold' : 'normal',
      shadow: { offsetX: 1, offsetY: 2, color: '#000000', blur: 3, fill: true },
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    const onHover = () => {
      bg.setFillStyle(isPrimary ? 0x2a130a : 0x1a120a, 1);
      topLine.setFillStyle(isPrimary ? 0xc87040 : 0xa77a48, 1);
      botLine.setFillStyle(isPrimary ? 0xc87040 : 0xa77a48, 1);
      ornL.setColor(isPrimary ? '#c87040' : '#a77a48').setAlpha(1);
      ornR.setColor(isPrimary ? '#c87040' : '#a77a48').setAlpha(1);
      txt.setColor(isPrimary ? '#ffe8a0' : '#c8973a');
      bg.setScale(1.02);
      txt.setScale(1.02);
    };
    const onOut = () => {
      bg.setFillStyle(isPrimary ? 0x1a0608 : 0x0d0a08, 0.92);
      topLine.setFillStyle(borderColor, 0.85);
      botLine.setFillStyle(borderColor, 0.85);
      ornL.setColor(ornColor).setAlpha(0.9);
      ornR.setColor(ornColor).setAlpha(0.9);
      txt.setColor(txtColor);
      bg.setScale(1);
      txt.setScale(1);
    };

    // Button-click SFX disabled — current clip reads off-tone. Re-enable by
    // wrapping onClick with `new SoundManager(this).play('ui_click', ...)`
    // once a better clip lands.
    for (const obj of [bg, txt]) {
      obj.on('pointerover', onHover);
      obj.on('pointerout', onOut);
      obj.on('pointerdown', onClick);
    }
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Sigil Vendor popup — Whiskers' shop. Compact side-by-side layout: stock
  // on the left, owned sigils (equip / recycle) on the right.
  // ══════════════════════════════════════════════════════════════════════════
  private showSigilVendorPopup(): void {
    const W = GAME_W, H = GAME_H;
    const cx = W / 2, cy = H / 2;
    const elements: Phaser.GameObjects.GameObject[] = [];
    // Floating tooltip tracked separately — always cleared on close/hover-off so it never leaks.
    let floatingTip: Phaser.GameObjects.Container | Phaser.GameObjects.Text | null = null;
    const clearTip = () => { floatingTip?.destroy(); floatingTip = null; };
    const showTip = (tipX: number, tipY: number, lines: string[]) => {
      clearTip();
      floatingTip = createHudTooltip(this, tipX, tipY, lines.join('\n'),
        0.5, 1, { fontSize: '9px', wrap: 220, depth: 110 });
    };
    const addEl = <T extends Phaser.GameObjects.GameObject>(o: T): T => { elements.push(o); return o; };
    const DEPTH = 80;

    // Enlarged panel — top half holds Finds (left) + equipped slots (right); bottom half is the
    // scrollable collection grid. Roomier than the old 540×360 two-column layout.
    const PW = 720, PH = 500;
    const panelTop = cy - PH / 2;
    const panelBottom = cy + PH / 2;

    addEl(this.add.rectangle(cx, cy, W, H, 0x000000, 0.78).setDepth(DEPTH).setInteractive()
      .on('pointerdown', () => closePopup()));
    addEl(this.add.rectangle(cx, cy, PW, PH, 0x0e0b08, 0.98).setStrokeStyle(1, 0x3d2a0a).setDepth(DEPTH + 1));

    // Title + feline subtitle
    addEl(this.add.text(cx, panelTop + 18, '✦  RUDY  ✦', {
      fontSize: '15px', fontFamily: "'Cinzel Decorative', Georgia, serif", color: '#f0c040', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(DEPTH + 2));
    addEl(this.add.text(cx, panelTop + 34, 'Purveyor of Whisker-Sworn Sigils', {
      fontSize: '8px', fontFamily: 'Georgia, serif', fontStyle: 'italic', color: '#7a5a30',
    }).setOrigin(0.5).setDepth(DEPTH + 2));

    // Close button
    const closeBtn = addEl(this.add.text(cx + PW / 2 - 14, panelTop + 12, '✕', {
      fontSize: '13px', fontFamily: 'Georgia, serif', color: '#5a3600',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setDepth(DEPTH + 3));
    closeBtn.on('pointerover', () => closeBtn.setColor('#c8973a'));
    closeBtn.on('pointerout',  () => closeBtn.setColor('#5a3600'));
    // Multi-select for bulk recycle — right-click toggles membership; a Recycle button
    // below the grid shows a confirmation popup with the combined refund.
    const selectedRecycleIds = new Set<string>();

    const closePopup = () => {
      refreshTimer?.remove();
      clearTip();
      selectedRecycleIds.clear();
      elements.forEach((e) => e.destroy());
      stockEls.forEach((e) => e.destroy());
      slotEls.forEach((e) => e.destroy());
      collectionEls.forEach((e) => e.destroy());
    };
    closeBtn.on('pointerdown', closePopup);

    // Header info: runes + countdown
    const runesTxt = addEl(this.add.text(cx, panelTop + 52,
      `◆ ${this.gsm.player.resources.loot ?? 0} runes   ·   ${GameStateManager.SIGIL_VENDOR_PRICE} per sigil   ·   recycle +${GameStateManager.SIGIL_RECYCLE_REFUND}`, {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: '#a07840',
    }).setOrigin(0.5).setDepth(DEPTH + 2));
    // Defensive setText helper — Phaser's setText hits texture.drawImage
    // and crashes if the Text was destroyed by a parallel close path
    // (insurance popup → close vendor → late callback → setText on dead
    // ref). Bail silently on destroyed/scene-less text.
    const safeRunesTxt = (s: string): void => {
      if (!runesTxt || !runesTxt.active || !runesTxt.scene) return;
      try { runesTxt.setText(s); } catch { /* destroyed mid-frame */ }
    };
    const countdownTxt = addEl(this.add.text(cx, panelTop + 66, '', {
      fontSize: '8px', fontFamily: 'Georgia, serif', color: '#7a5a30',
    }).setOrigin(0.5).setDepth(DEPTH + 2));

    // Equipping requires an active save slot with at least one hero. Buying doesn't — bought
    // sigils go into the account-wide collection and become equippable once a slot is chosen.
    const canEquipHere = (): boolean =>
      this.gsm.player.saveSlot >= 1 && (this.gsm.player.heroes?.length ?? 0) > 0;

    // ── Layout ────────────────────────────────────────────────────────────
    //   Top band  : header + runes + countdown (already placed above)
    //   Mid band  : LEFT = 3 "Today's Finds" rows  |  RIGHT = 3 equipped slot cards
    //   Horizontal divider at panelTop + 280
    //   Bottom    : collection icon grid with sort + scroll
    const midLeftX  = cx - PW / 2 + 200;
    const midRightX = cx + PW / 2 - 200;
    addEl(this.add.rectangle(cx, panelTop + 180, 1, 170, 0x3d2a0a, 0.6).setDepth(DEPTH + 2));
    addEl(this.add.rectangle(cx, panelTop + 280, PW - 36, 1, 0x3d2a0a, 0.6).setDepth(DEPTH + 2));

    addEl(this.add.text(midLeftX, panelTop + 88, "Today's Finds", {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(DEPTH + 2));
    const bagHeader = addEl(this.add.text(midRightX, panelTop + 88, '', {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(DEPTH + 2));

    // ── Today's Finds (stock) — 3 mystery rows ────────────────────────────
    const STOCK_ROW_W = 280, STOCK_ROW_H = 44, STOCK_ROW_GAP = 8;
    const stockListTop = panelTop + 108;
    let stockEls: Phaser.GameObjects.GameObject[] = [];
    const renderStock = () => {
      stockEls.forEach((e) => e.destroy());
      stockEls = [];
      const stock = this.gsm.account.sigilVendor?.offered ?? [];
      if (stock.length === 0) {
        stockEls.push(this.add.text(midLeftX, stockListTop + 60, 'Rudy is napping…\nNew stock on the clock.', {
          fontSize: '10px', fontFamily: 'Georgia, serif', color: '#6b4020', align: 'center',
        }).setOrigin(0.5).setDepth(DEPTH + 3));
        return;
      }
      const canAfford = (this.gsm.player.resources.loot ?? 0) >= GameStateManager.SIGIL_VENDOR_PRICE;
      stock.forEach((_sigil, i) => {
        const rowY = stockListTop + i * (STOCK_ROW_H + STOCK_ROW_GAP) + STOCK_ROW_H / 2;
        const rowBg = this.add.rectangle(midLeftX, rowY, STOCK_ROW_W, STOCK_ROW_H, 0x1a120a, 0.85)
          .setStrokeStyle(1, canAfford ? 0x8b6c30 : 0x3a2a18).setDepth(DEPTH + 3)
          .setInteractive({ useHandCursor: canAfford });
        stockEls.push(rowBg);
        stockEls.push(this.add.text(midLeftX - STOCK_ROW_W / 2 + 24, rowY, '?', {
          fontSize: '22px', fontFamily: 'Georgia, serif', color: canAfford ? '#f0c040' : '#6b4020', fontStyle: 'bold',
        }).setOrigin(0.5).setDepth(DEPTH + 4));
        stockEls.push(this.add.text(midLeftX - STOCK_ROW_W / 2 + 46, rowY - 9, 'Mystery Sigil', {
          fontSize: '11px', fontFamily: 'Georgia, serif', color: canAfford ? '#c8973a' : '#6b4020', fontStyle: 'bold',
        }).setOrigin(0, 0.5).setDepth(DEPTH + 4));
        stockEls.push(this.add.text(midLeftX - STOCK_ROW_W / 2 + 46, rowY + 7, 'Name & affixes revealed on purchase.', {
          fontSize: '7px', fontFamily: 'Georgia, serif', color: canAfford ? '#a07840' : '#4a3010',
        }).setOrigin(0, 0.5).setDepth(DEPTH + 4));
        stockEls.push(this.add.text(midLeftX + STOCK_ROW_W / 2 - 8, rowY, `${GameStateManager.SIGIL_VENDOR_PRICE} ◆`, {
          fontSize: '11px', fontFamily: 'Georgia, serif', color: canAfford ? '#fbbf24' : '#6b4020', fontStyle: 'bold',
        }).setOrigin(1, 0.5).setDepth(DEPTH + 4));
        rowBg.on('pointerover', () => {
          rowBg.setFillStyle(0x2a1f10, 0.9);
          showTip(midLeftX, rowY - STOCK_ROW_H / 2 - 4, ['Mystery sigil — contents hidden.', 'Buy to reveal and add to your collection.']);
        });
        rowBg.on('pointerout', () => { rowBg.setFillStyle(0x1a120a, 0.85); clearTip(); });
        rowBg.on('pointerdown', () => {
          if (!canAfford) return;
          if (this.gsm.buySigil(i) === 'ok') {
            clearTip();
            safeRunesTxt(`◆ ${this.gsm.player.resources.loot ?? 0} runes   ·   ${GameStateManager.SIGIL_VENDOR_PRICE} per sigil   ·   recycle +${GameStateManager.SIGIL_RECYCLE_REFUND}`);
            renderStock();
            renderCollection();
            renderSlots();
          }
        });
      });
    };

    // ── Equipped slot cards (right side of mid band) — 3 cards side by side ─
    const SLOT_CARD_W = 92, SLOT_CARD_H = 126, SLOT_CARD_GAP = 10;
    const slotStripW = 3 * SLOT_CARD_W + 2 * SLOT_CARD_GAP;
    const slotStripStartX = midRightX - slotStripW / 2 + SLOT_CARD_W / 2;
    const slotCardY = panelTop + 108 + SLOT_CARD_H / 2;
    let slotEls: Phaser.GameObjects.GameObject[] = [];
    const renderSlots = () => {
      slotEls.forEach((e) => e.destroy());
      slotEls = [];
      const equipped = this.gsm.player.sigilsEquipped ?? [];
      const slots = this.gsm.effectiveSigilSlots();
      if (!canEquipHere()) {
        bagHeader.setText('Select a save slot to equip sigils');
        bagHeader.setColor('#c84040');
      } else {
        bagHeader.setText(`Your Sigils — ${equipped.length}/${slots} equipped`);
        bagHeader.setColor('#c8973a');
      }
      const bag = this.gsm.player.sigilBag ?? [];
      const coll = this.gsm.account.sigilCollection ?? [];
      for (let s = 0; s < 3; s++) {
        const cx2 = slotStripStartX + s * (SLOT_CARD_W + SLOT_CARD_GAP);
        const unlocked = s < slots;
        const sigilId = equipped[s];
        const sigil = sigilId ? bag.find((b) => b.id === sigilId) ?? coll.find((b) => b.id === sigilId) : undefined;

        const card = this.add.rectangle(cx2, slotCardY, SLOT_CARD_W, SLOT_CARD_H,
          unlocked ? (sigil ? 0x2a1f10 : 0x140e06) : 0x0a0806, 0.9,
        ).setStrokeStyle(1, unlocked ? (sigil ? 0xc0912a : 0x3a2a18) : 0x1a1208).setDepth(DEPTH + 3);
        slotEls.push(card);
        slotEls.push(this.add.text(cx2 - SLOT_CARD_W / 2 + 4, slotCardY - SLOT_CARD_H / 2 + 3,
          `${s + 1}`, {
            fontSize: '9px', fontFamily: 'monospace', color: unlocked ? '#c8973a' : '#4a3010', fontStyle: 'bold',
          }).setOrigin(0, 0).setDepth(DEPTH + 4));

        if (!unlocked) {
          // Subtle etched-rune background — no padlock glyph. The darker background + dim text is
          // what signals "locked"; the rune glyph just gives the card some visual weight.
          slotEls.push(this.add.text(cx2, slotCardY - 18, '◆', {
            fontSize: '28px', color: '#2a1a0a',
          }).setOrigin(0.5).setAlpha(0.35).setDepth(DEPTH + 4));
          const req = s === 1 ? 'Whisker Bond\nStage 20' : 'Feline Pact\nStage 40';
          slotEls.push(this.add.text(cx2, slotCardY + 22, req, {
            fontSize: '9px', fontFamily: 'Georgia, serif', color: '#5a3600', align: 'center', fontStyle: 'bold',
          }).setOrigin(0.5).setDepth(DEPTH + 4));
          continue;
        }

        if (sigil) {
          if (this.textures.exists(sigil.special.iconKey)) {
            slotEls.push(this.add.image(cx2, slotCardY - 26, sigil.special.iconKey)
              .setDisplaySize(46, 46).setDepth(DEPTH + 4));
          }
          slotEls.push(this.add.text(cx2, slotCardY + 2, sigil.name, {
            fontSize: '9px', fontFamily: 'Georgia, serif', color: '#f0c040', fontStyle: 'bold',
            align: 'center', wordWrap: { width: SLOT_CARD_W - 10 },
          }).setOrigin(0.5, 0).setDepth(DEPTH + 4));
          const unequipBtn = this.add.text(cx2, slotCardY + SLOT_CARD_H / 2 - 12, 'unequip', {
            fontSize: '8px', fontFamily: 'Georgia, serif', color: '#e8a030',
            backgroundColor: '#1a0608', padding: { x: 4, y: 1 },
          }).setOrigin(0.5).setDepth(DEPTH + 5).setInteractive({ useHandCursor: true });
          slotEls.push(unequipBtn);
          unequipBtn.on('pointerdown', () => {
            if (!canEquipHere()) return;
            this.gsm.unequipSigil(sigil.id);
            renderSlots();
            renderCollection();
          });
          card.setInteractive({ useHandCursor: false });
          card.on('pointerover', () => showTip(cx2, slotCardY - SLOT_CARD_H / 2 - 4,
            [sigil.name, sigil.special.description, sigil.affixes.map(formatAffix).join(', ')]));
          card.on('pointerout', () => clearTip());
          // Double-click the slot card to unequip — quick action without hunting for the chip.
          let lastCardClickAt = 0;
          card.on('pointerdown', () => {
            if (!canEquipHere()) return;
            const now = Date.now();
            if (now - lastCardClickAt < 400) {
              this.gsm.unequipSigil(sigil.id);
              renderSlots();
              renderCollection();
              lastCardClickAt = 0;
            } else {
              lastCardClickAt = now;
            }
          });
        } else {
          slotEls.push(this.add.text(cx2, slotCardY - 14, '·', {
            fontSize: '28px', fontFamily: 'Georgia, serif', color: '#3a2a18',
          }).setOrigin(0.5).setDepth(DEPTH + 4));
          slotEls.push(this.add.text(cx2, slotCardY + 22, 'Empty\nclick a sigil below', {
            fontSize: '8px', fontFamily: 'Georgia, serif', color: '#5a3600', align: 'center',
            wordWrap: { width: SLOT_CARD_W - 10 },
          }).setOrigin(0.5).setDepth(DEPTH + 4));
        }
      }
    };

    // ── Collection icon grid (bottom half) ─────────────────────────────────
    const gridHeaderY = panelTop + 292;
    const gridTop = panelTop + 320;
    const CELL = 36, CELL_GAP = 6, COLS = 8, VISIBLE_ROWS = 4;
    const gridW = COLS * CELL + (COLS - 1) * CELL_GAP;
    const gridStartX = cx - gridW / 2 + CELL / 2;

    let collectionEls: Phaser.GameObjects.GameObject[] = [];
    let gridScroll = 0;
    type SigilSort = 'newest' | 'oldest' | 'name';
    let bagSort: SigilSort = 'newest';
    const collectionHeader = addEl(this.add.text(cx - PW / 2 + 18, gridHeaderY, '', {
      fontSize: '10px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
    }).setOrigin(0, 0).setDepth(DEPTH + 2));

    const sortOpts: Array<{ key: SigilSort; label: string }> = [
      { key: 'newest', label: '⏱ New' },
      { key: 'oldest', label: '⏱ Old' },
      { key: 'name',   label: 'A–Z'  },
    ];
    const sortButtons: Phaser.GameObjects.Text[] = [];
    sortOpts.forEach((opt, oi) => {
      const bx = cx + PW / 2 - 20 - (sortOpts.length - 1 - oi) * 44;
      const btn = addEl(this.add.text(bx, gridHeaderY + 2, opt.label, {
        fontSize: '9px', fontFamily: 'Georgia, serif',
        color: bagSort === opt.key ? '#f0c040' : '#7a5a30',
        backgroundColor: bagSort === opt.key ? '#2a1f10' : '#0d0a08',
        padding: { x: 4, y: 2 },
      }).setOrigin(1, 0).setDepth(DEPTH + 3).setInteractive({ useHandCursor: true }));
      sortButtons.push(btn);
      btn.on('pointerdown', () => {
        bagSort = opt.key;
        gridScroll = 0;
        sortButtons.forEach((b, bi) => {
          b.setColor(bagSort === sortOpts[bi].key ? '#f0c040' : '#7a5a30');
          b.setBackgroundColor(bagSort === sortOpts[bi].key ? '#2a1f10' : '#0d0a08');
        });
        renderCollection();
      });
    });

    const renderCollection = () => {
      collectionEls.forEach((e) => e.destroy());
      collectionEls = [];
      const savebag = this.gsm.player.sigilBag ?? [];
      const collection = this.gsm.account.sigilCollection ?? [];
      const seen = new Set<string>();
      const merged: typeof savebag = [];
      for (const s of [...collection, ...savebag]) {
        if (seen.has(s.id)) continue;
        seen.add(s.id);
        merged.push(s);
      }
      if (bagSort === 'name') merged.sort((a, b) => a.name.localeCompare(b.name));
      else if (bagSort === 'newest') merged.sort((a, b) => (b.acquiredAt ?? 0) - (a.acquiredAt ?? 0));
      else merged.sort((a, b) => (a.acquiredAt ?? 0) - (b.acquiredAt ?? 0));

      collectionHeader.setText(`Collection (${merged.length})`);

      if (merged.length === 0) {
        collectionEls.push(this.add.text(cx, gridTop + 50, 'Your collection is empty.\nBuy a mystery sigil above or bring loot to the in-battle cat.', {
          fontSize: '10px', fontFamily: 'Georgia, serif', color: '#5a3600', align: 'center',
        }).setOrigin(0.5).setDepth(DEPTH + 3));
        return;
      }

      const equipped = new Set(this.gsm.player.sigilsEquipped ?? []);
      const slots = this.gsm.effectiveSigilSlots();
      const maxRow = Math.ceil(merged.length / COLS);
      const maxScroll = Math.max(0, maxRow - VISIBLE_ROWS);
      if (gridScroll > maxScroll) gridScroll = maxScroll;
      if (gridScroll < 0) gridScroll = 0;

      const pageStart = gridScroll * COLS;
      const pageEnd = Math.min(merged.length, pageStart + VISIBLE_ROWS * COLS);

      for (let k = pageStart; k < pageEnd; k++) {
        const sigil = merged[k];
        const posInPage = k - pageStart;
        const row = Math.floor(posInPage / COLS);
        const col = posInPage % COLS;
        const ix = gridStartX + col * (CELL + CELL_GAP);
        const iy = gridTop + row * (CELL + CELL_GAP);
        const isEq = equipped.has(sigil.id);
        // Locked = another save slot is already wearing this account-collection
        // sigil. Each account-collection sigil is single-use across all slots.
        const lockedBySlot = !isEq ? this.gsm.sigilEquippedByOtherSlot(sigil.id) : null;
        const isLocked = lockedBySlot !== null;

        const isSelected = selectedRecycleIds.has(sigil.id);
        // Border colour priority: locked (red) > selected (ember) > equipped (gold) > default.
        const borderCol = isLocked ? 0xc82040
          : isSelected ? 0xef4444
            : isEq ? 0xc0912a
              : 0x3a2a18;
        const baseFill = isLocked ? 0x1a0808 : isEq ? 0x2a1f10 : 0x140e06;
        const cellBg = this.add.rectangle(ix, iy, CELL, CELL, baseFill, 0.9)
          .setStrokeStyle(isSelected || isLocked ? 2 : 1, borderCol).setDepth(DEPTH + 3)
          .setInteractive({ useHandCursor: !isLocked });
        collectionEls.push(cellBg);
        if (this.textures.exists(sigil.special.iconKey)) {
          const icon = this.add.image(ix, iy, sigil.special.iconKey)
            .setDisplaySize(CELL - 6, CELL - 6).setDepth(DEPTH + 4);
          if (isLocked) icon.setTint(0x803040).setAlpha(0.55);
          collectionEls.push(icon);
        } else {
          const glyph = this.add.text(ix, iy, '◆', { fontSize: '16px', color: isLocked ? '#803040' : '#f0c040' })
            .setOrigin(0.5).setDepth(DEPTH + 4);
          if (isLocked) glyph.setAlpha(0.55);
          collectionEls.push(glyph);
        }
        if (isEq) {
          collectionEls.push(this.add.text(ix + CELL / 2 - 2, iy - CELL / 2 + 2, '✓', {
            fontSize: '10px', fontFamily: 'Georgia, serif', color: '#f0c040', fontStyle: 'bold',
          }).setOrigin(1, 0).setDepth(DEPTH + 5));
        }
        if (isLocked) {
          // Slot-number badge — red pill in the top-right corner tells the player
          // which save slot is currently wearing this collection sigil. No emoji.
          const badgeBg = this.add.rectangle(ix + CELL / 2 - 1, iy - CELL / 2 + 1, 14, 11, 0x601020, 0.95)
            .setStrokeStyle(1, 0xef4444).setOrigin(1, 0).setDepth(DEPTH + 5);
          collectionEls.push(badgeBg);
          collectionEls.push(this.add.text(ix + CELL / 2 - 8, iy - CELL / 2 + 6, `S${lockedBySlot}`, {
            fontSize: '8px', fontFamily: 'Georgia, serif', color: '#ffdce0', fontStyle: 'bold',
          }).setOrigin(0.5).setDepth(DEPTH + 6));
        }
        if (isSelected) {
          // Small crimson circle in the opposite corner so selected sigils stand out at a glance.
          collectionEls.push(this.add.text(ix - CELL / 2 + 2, iy + CELL / 2 - 2, '●', {
            fontSize: '9px', fontFamily: 'Georgia, serif', color: '#ef4444',
          }).setOrigin(0, 1).setDepth(DEPTH + 5));
        }

        cellBg.on('pointerover', () => {
          cellBg.setFillStyle(isLocked ? 0x2a1010 : isEq ? 0x3a2a18 : 0x2a1f10, 0.95);
          const hint = isLocked
            ? `Equipped in save slot ${lockedBySlot} — unequip it there first`
            : isEq
              ? '(equipped — click to unequip)'
              : (isSelected
                ? 'Right-click to deselect  ·  Left-click to equip'
                : 'Left-click to equip  ·  Right-click to select for recycle');
          showTip(ix, iy - CELL / 2 - 4,
            [sigil.name, sigil.special.description, sigil.affixes.map(formatAffix).join(', '), hint]);
        });
        cellBg.on('pointerout', () => { cellBg.setFillStyle(baseFill, 0.9); clearTip(); });
        cellBg.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
          if (isLocked) {
            // Hard block — another save slot owns this collection sigil right now.
            showTip(ix, iy - CELL / 2 - 4,
              [sigil.name, `Equipped in save slot ${lockedBySlot}`, 'Unequip it there first.']);
            return;
          }
          if (pointer.rightButtonDown()) {
            // Right-click toggles selection — equipped sigils can't be recycled and are skipped.
            if (isEq) return;
            if (selectedRecycleIds.has(sigil.id)) selectedRecycleIds.delete(sigil.id);
            else selectedRecycleIds.add(sigil.id);
            clearTip();
            renderCollection();
            return;
          }
          // Equip/unequip requires an active save slot with a hero — collection is account-wide,
          // but equipping ties the sigil to a specific hero run. Bought sigils are already stored
          // on the account, so they'll show up once a slot is selected.
          if (!canEquipHere()) {
            showTip(ix, iy - CELL / 2 - 4, ['Select a save slot with a hero', 'before equipping sigils.']);
            return;
          }
          if (isEq) {
            this.gsm.unequipSigil(sigil.id);
          } else if ((this.gsm.player.sigilsEquipped?.length ?? 0) >= slots) {
            showTip(ix, iy - CELL / 2 - 4, ['All sigil slots full.', 'Unequip a slot first.']);
            return;
          } else {
            // Run-lock: once any wave has fired (waveNumber >= 1, OR currentLevel
            // > 1, OR stayCount > 0), the player's loadout is locked. Previous
            // behaviour showed a subtle tooltip that clicks could miss; now a
            // full popup makes the "it's too late" rule unmissable. Message
            // varies based on whether the player has already committed any
            // sigils to this run: if they did, we frame it as "loadout locked";
            // if not, we frame it as "run already in progress, too late to
            // add sigils".
            const inBag = this.gsm.player.sigilBag?.some((s) => s.id === sigil.id) ?? false;
            if (this.gsm.isRunActive() && !inBag) {
              const hasCommitted = (this.gsm.player.sigilsEquipped?.length ?? 0) > 0
                || (this.gsm.player.sigilBag?.length ?? 0) > 0;
              const msg = hasCommitted
                ? 'Your sigil loadout is locked for this run.\nFinish the run (or die) to swap\ncollection sigils again.'
                : 'The run has already started.\nIt\'s too late to bring a new sigil\nfrom the collection — only looted\nsigils can be equipped now.';
              clearTip();
              this.showSigilLockedPopup(msg);
              return;
            }
            // Pre-run loadout window (fresh save slot — stage 1, wave 0, no
            // stays) and the sigil is a collection pick. Offer an insurance
            // prompt BEFORE committing so the player can choose to lock it in
            // with 10 runes (sigil moves to bag, returns on death) or equip
            // uninsured (sigil stays safe in the collection, no combat risk).
            const freshSlot = !this.gsm.isRunActive()
              && this.gsm.player.currentLevel === 1
              && this.gsm.battle.waveNumber === 0
              && (this.gsm.player.stayCount ?? 0) === 0;
            if (freshSlot && !inBag) {
              clearTip();
              this.showSigilInsurancePrompt(sigil.id, sigil.name, () => {
                renderCollection();
                renderSlots();
              });
              return;
            }
            this.gsm.equipSigil(sigil.id);
          }
          clearTip();
          renderCollection();
          renderSlots();
        });
      }

      // ── Recycle Selected button — only shown when at least one sigil is selected.
      //    Opens a confirm popup before actually recycling; Esc / Cancel clears nothing.
      if (selectedRecycleIds.size > 0) {
        const refund = selectedRecycleIds.size * GameStateManager.SIGIL_RECYCLE_REFUND;
        const btnY = gridHeaderY + 2;
        const btnX = cx - PW / 2 + 18 + 120; // right of the "Collection (N)" header
        const recycleBtn = this.add.text(btnX, btnY,
          `♻ Recycle ${selectedRecycleIds.size}  (+${refund}◆)`, {
          fontSize: '9px', fontFamily: 'Georgia, serif', color: '#ff8030', fontStyle: 'bold',
          backgroundColor: '#1a0608', padding: { x: 6, y: 2 },
        }).setOrigin(0, 0).setDepth(DEPTH + 3).setInteractive({ useHandCursor: true });
        collectionEls.push(recycleBtn);
        recycleBtn.on('pointerover', () => { recycleBtn.setColor('#ffb060'); recycleBtn.setBackgroundColor('#2a0a0a'); });
        recycleBtn.on('pointerout',  () => { recycleBtn.setColor('#ff8030'); recycleBtn.setBackgroundColor('#1a0608'); });
        recycleBtn.on('pointerdown', () => {
          const count = selectedRecycleIds.size;
          // Inline confirm popup — MenuScene doesn't have a reusable showConfirm helper yet.
          const confirmEls: Phaser.GameObjects.GameObject[] = [];
          const dim = this.add.rectangle(cx, cy, W, H, 0x000000, 0.6).setDepth(DEPTH + 60).setInteractive();
          confirmEls.push(dim);
          confirmEls.push(this.add.rectangle(cx, cy, 340, 150, 0x0d0a08, 0.98)
            .setStrokeStyle(2, 0x8b2000).setDepth(DEPTH + 61));
          confirmEls.push(this.add.text(cx, cy - 36, `Recycle ${count} sigil${count === 1 ? '' : 's'}?`, {
            fontSize: '13px', fontFamily: 'Georgia, serif', color: '#ef4444', fontStyle: 'bold',
          }).setOrigin(0.5).setDepth(DEPTH + 62));
          confirmEls.push(this.add.text(cx, cy - 10, `Refund: +${refund}◆ runes`, {
            fontSize: '10px', fontFamily: 'Georgia, serif', color: '#a07840',
          }).setOrigin(0.5).setDepth(DEPTH + 62));
          const yesBg = this.add.rectangle(cx - 60, cy + 32, 120, 30, 0x1a0608, 1)
            .setStrokeStyle(2, 0x8b2000).setDepth(DEPTH + 62).setInteractive({ useHandCursor: true });
          const yesTxt = this.add.text(cx - 60, cy + 32, 'RECYCLE', {
            fontSize: '11px', fontFamily: 'Georgia, serif', color: '#ff8030', fontStyle: 'bold',
          }).setOrigin(0.5).setDepth(DEPTH + 63).setInteractive({ useHandCursor: true });
          const noBg = this.add.rectangle(cx + 60, cy + 32, 100, 30, 0x0d0a08, 1)
            .setStrokeStyle(2, 0x5a3600).setDepth(DEPTH + 62).setInteractive({ useHandCursor: true });
          const noTxt = this.add.text(cx + 60, cy + 32, 'CANCEL', {
            fontSize: '11px', fontFamily: 'Georgia, serif', color: '#a07840',
          }).setOrigin(0.5).setDepth(DEPTH + 63).setInteractive({ useHandCursor: true });
          confirmEls.push(yesBg, yesTxt, noBg, noTxt);
          const cleanup = () => confirmEls.forEach((o) => o.destroy());
          const doYes = () => {
            cleanup();
            for (const id of selectedRecycleIds) this.gsm.recycleSigil(id);
            selectedRecycleIds.clear();
            clearTip();
            safeRunesTxt(`◆ ${this.gsm.player.resources.loot ?? 0} runes   ·   ${GameStateManager.SIGIL_VENDOR_PRICE} per sigil   ·   recycle +${GameStateManager.SIGIL_RECYCLE_REFUND}`);
            renderCollection();
            renderSlots();
          };
          yesBg.on('pointerdown', doYes);
          yesTxt.on('pointerdown', doYes);
          noBg.on('pointerdown', cleanup);
          noTxt.on('pointerdown', cleanup);
        });
      }

      if (maxRow > VISIBLE_ROWS) {
        const arrowY = gridTop + VISIBLE_ROWS * (CELL + CELL_GAP) + 2;
        const up = this.add.text(cx - 28, arrowY, '▲', {
          fontSize: '11px', fontFamily: 'Georgia, serif',
          color: gridScroll > 0 ? '#c8973a' : '#4a3010',
        }).setOrigin(0.5).setDepth(DEPTH + 3).setInteractive({ useHandCursor: gridScroll > 0 });
        collectionEls.push(up);
        up.on('pointerdown', () => { if (gridScroll > 0) { gridScroll--; renderCollection(); } });
        const dn = this.add.text(cx + 28, arrowY, '▼', {
          fontSize: '11px', fontFamily: 'Georgia, serif',
          color: gridScroll < maxScroll ? '#c8973a' : '#4a3010',
        }).setOrigin(0.5).setDepth(DEPTH + 3).setInteractive({ useHandCursor: gridScroll < maxScroll });
        collectionEls.push(dn);
        dn.on('pointerdown', () => { if (gridScroll < maxScroll) { gridScroll++; renderCollection(); } });
        const pageText = this.add.text(cx, arrowY, `Rows ${gridScroll + 1}–${Math.min(gridScroll + VISIBLE_ROWS, maxRow)} / ${maxRow}`, {
          fontSize: '8px', fontFamily: 'Georgia, serif', color: '#6b4020',
        }).setOrigin(0.5).setDepth(DEPTH + 3);
        collectionEls.push(pageText);
      }
    };

    renderStock();
    renderSlots();
    renderCollection();

    // Countdown ticker — never resets the vendor timer; refreshSigilVendor() only rerolls when expired.
    const fmtDur = (ms: number) => {
      const s = Math.ceil(ms / 1000);
      const m = Math.floor(s / 60), sec = s % 60;
      return `${m}:${String(sec).padStart(2, '0')}`;
    };
    const updateCountdown = () => {
      // Defensive: if the popup closed but this timer fired one more tick
      // before the scheduler removes it, setText on a destroyed Text crashes
      // with "drawImage of null" inside Phaser's frame UV update. Bail
      // silently when the text is gone.
      if (!countdownTxt || !countdownTxt.active || !countdownTxt.scene) return;
      const ms = this.gsm.sigilVendorMsUntilRefresh();
      if (ms === 0) {
        this.gsm.refreshSigilVendor();
        renderStock();
      }
      try {
        countdownTxt.setText(`New stock in ${fmtDur(this.gsm.sigilVendorMsUntilRefresh())}`);
      } catch { /* text destroyed mid-frame */ }
    };
    updateCountdown();
    const refreshTimer = this.time.addEvent({ delay: 1000, loop: true, callback: updateCountdown });
  }
}
